// Canonical AVRASTRO OS data model — schema version 1.
//
// One source of truth: every entity has (1) a TypeScript interface and (2) a field
// descriptor table. The descriptor table drives runtime validation, the SQLite DDL
// (packages/store), IndexedDB stores, backup manifests and the legacy importer.
// `satisfies Fields<T>` guarantees the two can never drift apart.

export const SCHEMA_VERSION = 18;

export type FieldType = 'id' | 'ref' | 'text' | 'date' | 'time' | 'datetime' | 'number' | 'int' | 'bool' | 'enum' | 'json';
export interface FieldDef {
  t: FieldType;
  req?: boolean;
  values?: readonly string[];
  /** create a secondary index (SQLite) / used for fast lookups */
  index?: boolean;
  /** foreign table for ref fields (integrity checks) */
  ref?: string;
}
export type Fields<T> = { [K in keyof T]-?: FieldDef };

// ---------------------------------------------------------------------------
// Enumerations (stable machine values; labels live in i18n under enum.<name>.<value>)

export const PERSON_KINDS = ['client', 'prospect', 'contact', 'dependent'] as const;
export const PERSON_STAGES = ['lead', 'prospect', 'client', 'former', 'dependent'] as const;
export const POLICY_BRANCHES = ['life', 'general'] as const;
export const POLICY_STATUSES = ['offer', 'pending', 'active', 'renewed', 'lapsed', 'cancelled', 'matured'] as const;
export const PAYMENT_FREQUENCIES = ['monthly', 'quarterly', 'semiannual', 'annual', 'single'] as const;
export const CASE_STAGES = ['draft', 'submitted', 'documents', 'underwriting', 'decision', 'issued', 'declined', 'postponed', 'withdrawn'] as const;
export const CASE_MAIN_STAGES = ['draft', 'submitted', 'documents', 'underwriting', 'decision', 'issued'] as const;
export const CASE_TERMINAL_STAGES = ['issued', 'declined', 'postponed', 'withdrawn'] as const;
export const REQUIREMENT_CATEGORIES = ['identity', 'address', 'bank', 'questionnaire', 'financial', 'medical', 'signature', 'other'] as const;
export const TASK_KINDS = ['task', 'call', 'followup', 'appointment', 'review', 'document', 'renewal', 'application', 'other'] as const;
export const TASK_STATUSES = ['open', 'done', 'cancelled'] as const;
export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const LOCATION_TYPES = ['office', 'client', 'online', 'phone', 'custom'] as const;
export const REVIEW_OUTCOMES = ['completed', 'open', 'needs_followup', 'no_change', 'declined'] as const;
export const PROSPECT_STATUSES = ['new', 'called', 'no_answer', 'callback', 'interested', 'appointment', 'not_interested', 'wrong_number', 'converted'] as const;
export const HOUSEHOLD_ROLES = ['primary', 'spouse', 'child', 'dependent', 'other'] as const;
export const SERVICE_STATUSES = ['open', 'in_progress', 'waiting', 'closed'] as const;
export const TIMELINE_TYPES = ['created', 'note', 'call', 'message', 'email', 'meeting', 'appointment', 'task', 'policy', 'case', 'review', 'document', 'service', 'proposal', 'comparison', 'import', 'system', 'legacy'] as const;
export const OUTCOMES = ['positive', 'neutral', 'negative'] as const;
export const COMPARISON_STATUSES = ['draft', 'decided'] as const;
export const COMPARISON_DECISIONS = ['A', 'B', 'C', 'later', 'none'] as const;
// schema v3 — advisory depth (spec §8–§11, §20–§35)
export const NEED_AREAS = ['life', 'income', 'critical_illness', 'health', 'accident', 'mortgage', 'retirement', 'savings', 'education', 'property', 'business'] as const;
export const NEEDS_STATUSES = ['draft', 'final'] as const;
export const RISK_ATTITUDES = ['conservative', 'balanced', 'growth', 'unknown'] as const;
export const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired'] as const;
export const OPPORTUNITY_STAGES = ['lead', 'qualified', 'proposal', 'decision', 'won', 'lost', 'deferred'] as const;
export const REFERRAL_STATUSES = ['new', 'contacted', 'meeting', 'converted', 'declined'] as const;
export const COMM_CHANNELS = ['phone', 'email', 'sms', 'whatsapp', 'meeting', 'letter', 'social', 'other'] as const;
export const COMM_DIRECTIONS = ['out', 'in'] as const;
export const MEETING_STATUSES = ['planned', 'held'] as const;
export const CLAIM_STATUSES = ['open', 'submitted', 'pending_documents', 'approved', 'paid', 'declined', 'closed'] as const;
// schema v4 — business engine, tax center, campaigns, documents, sync (spec §98–§115)
export const POLICY_CATEGORIES = ['life', 'medical', 'savings', 'pension', 'education', 'accident', 'critical_illness', 'disability', 'credit_life', 'motor', 'home', 'travel', 'liability', 'business', 'group', 'other'] as const;
export const PARTY_ROLES = ['owner', 'insured', 'beneficiary', 'payer', 'advisor'] as const;
export const PRODUCTION_TYPES = ['new_business', 'increase', 'decrease', 'renewal', 'replacement', 'reactivation', 'cancellation', 'reversal'] as const;
export const RENEWAL_STATUSES = ['pending', 'quoted', 'renewed', 'lapsed', 'declined'] as const;
/** v8: general-insurance renewal pipeline (requested quotes → compared → sent to client → approved → renewed, or lost) */
export const GI_RENEWAL_STAGES = ['requested_quotes', 'compared', 'sent_to_client', 'approved', 'renewed', 'lost'] as const;
export const COMMISSION_KINDS = ['new_business', 'renewal', 'trail', 'bonus', 'campaign_bonus', 'adjustment'] as const;
export const COMMISSION_STATUSES = ['expected', 'pending', 'paid', 'cancelled'] as const;
export const TAX_RULE_STATUSES = ['approved', 'provisional', 'draft', 'retired'] as const;
export const FINANCIAL_KINDS = ['income', 'expense', 'liability', 'goal', 'asset'] as const;
export const LIFE_EVENT_TYPES = ['marriage', 'new_child', 'new_job', 'salary_increase', 'home_purchase', 'mortgage', 'new_loan', 'business_start', 'retirement_planning', 'divorce', 'new_property', 'other'] as const;
export const CAMPAIGN_STATUSES = ['draft', 'ready', 'active', 'completed', 'archived'] as const;
export const CAMPAIGN_ACTIVITY_TYPES = ['contacted', 'responded', 'appointment', 'quote', 'application', 'issued', 'opted_out'] as const;
export const EXTRACTION_STATUSES = ['pending', 'reviewed', 'applied', 'rejected', 'failed'] as const;
export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'choice', 'bool'] as const;
export const DOC_CATEGORIES = ['identity', 'policies', 'applications', 'tax', 'income', 'medical', 'loans', 'claims', 'quotations', 'signed', 'other'] as const;
export const SYNC_OP_STATES = ['pending', 'acked', 'rejected'] as const;
export const SYNC_CONFLICT_STATUSES = ['open', 'resolved'] as const;
// schema v5 — branding, document checklists, platform-facing profile
export const BRANDING_SCOPES = ['agency', 'advisor'] as const;
export const CO_BRANDING = ['co', 'advisor', 'vero'] as const;
export const REQUIREMENT_STATUSES = ['required', 'requested', 'received', 'waived', 'expired'] as const;
export const WAITING_ON = ['client', 'insurer', 'advisor'] as const;

export type PersonKind = typeof PERSON_KINDS[number];
export type PersonStage = typeof PERSON_STAGES[number];
export type PolicyBranch = typeof POLICY_BRANCHES[number];
export type PolicyStatus = typeof POLICY_STATUSES[number];
export type PaymentFrequency = typeof PAYMENT_FREQUENCIES[number];
export type CaseStage = typeof CASE_STAGES[number];
export type RequirementCategory = typeof REQUIREMENT_CATEGORIES[number];
export type TaskKind = typeof TASK_KINDS[number];
export type TaskStatus = typeof TASK_STATUSES[number];
export type Priority = typeof PRIORITIES[number];
export type LocationType = typeof LOCATION_TYPES[number];
export type ReviewOutcome = typeof REVIEW_OUTCOMES[number];
export type ProspectStatus = typeof PROSPECT_STATUSES[number];
export type HouseholdRole = typeof HOUSEHOLD_ROLES[number];
export type ServiceStatus = typeof SERVICE_STATUSES[number];
export type TimelineType = typeof TIMELINE_TYPES[number];
export type Outcome = typeof OUTCOMES[number];
export type ComparisonStatus = typeof COMPARISON_STATUSES[number];
export type ComparisonDecision = typeof COMPARISON_DECISIONS[number];
export type NeedArea = typeof NEED_AREAS[number];
export type NeedsStatus = typeof NEEDS_STATUSES[number];
export type RiskAttitude = typeof RISK_ATTITUDES[number];
export type QuoteStatus = typeof QUOTE_STATUSES[number];
export type OpportunityStage = typeof OPPORTUNITY_STAGES[number];
export type ReferralStatus = typeof REFERRAL_STATUSES[number];
export type CommChannel = typeof COMM_CHANNELS[number];
export type CommDirection = typeof COMM_DIRECTIONS[number];
export type MeetingStatus = typeof MEETING_STATUSES[number];
export type ClaimStatus = typeof CLAIM_STATUSES[number];
export type PolicyCategory = typeof POLICY_CATEGORIES[number];
export type PartyRole = typeof PARTY_ROLES[number];
export type ProductionType = typeof PRODUCTION_TYPES[number];
export type RenewalStatus = typeof RENEWAL_STATUSES[number];
export type GiRenewalStage = typeof GI_RENEWAL_STAGES[number];
export type CommissionKind = typeof COMMISSION_KINDS[number];
export type CommissionStatus = typeof COMMISSION_STATUSES[number];
export type TaxRuleStatus = typeof TAX_RULE_STATUSES[number];
export type FinancialKind = typeof FINANCIAL_KINDS[number];
export type LifeEventType = typeof LIFE_EVENT_TYPES[number];
export type CampaignStatus = typeof CAMPAIGN_STATUSES[number];
export type CampaignActivityType = typeof CAMPAIGN_ACTIVITY_TYPES[number];
export type ExtractionStatus = typeof EXTRACTION_STATUSES[number];
export type CustomFieldType = typeof CUSTOM_FIELD_TYPES[number];
export type DocCategory = typeof DOC_CATEGORIES[number];
export type SyncOpState = typeof SYNC_OP_STATES[number];
export type SyncConflictStatus = typeof SYNC_CONFLICT_STATUSES[number];
export type BrandingScope = typeof BRANDING_SCOPES[number];
export type CoBranding = typeof CO_BRANDING[number];
export type RequirementStatus = typeof REQUIREMENT_STATUSES[number];
export type WaitingOn = typeof WAITING_ON[number];
/** a party to a policy: the owner can differ from the insured (parent owns, child insured) */
export interface PolicyParty { role: PartyRole; personId?: string | null; name?: string | null; share?: number | null }
/** a benefit line of a policy (sum, term, covered person) — used by the protection map, never inferred from tags */
export interface PolicyBenefit { kind: string; amount?: number | null; termYears?: number | null; insuredPersonId?: string | null; note?: string | null; guaranteed?: boolean | null }

export interface ContactPoint { label: string; value: string }

// ---------------------------------------------------------------------------
// Common columns

interface Base {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** optimistic concurrency / sync version; incremented on every write */
  version: number;
}
const base = {
  id: { t: 'id', req: true },
  createdAt: { t: 'datetime', req: true },
  updatedAt: { t: 'datetime', req: true, index: true },
  version: { t: 'int', req: true },
} as const satisfies Record<keyof Base, FieldDef>;

/** extension attributes and the untouched legacy source record (lossless import) */
interface Ext { data?: Record<string, unknown> | null; legacy?: unknown }
const ext = { data: { t: 'json' }, legacy: { t: 'json' } } as const satisfies Record<keyof Ext, FieldDef>;

// ---------------------------------------------------------------------------
// Entities

export interface Person extends Base, Ext {
  kind: PersonKind;
  stage: PersonStage;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  gender?: string | null;
  birthDate?: string | null;
  /** manual name-day override, MM-DD */
  nameDay?: string | null;
  phones?: ContactPoint[] | null;
  emails?: ContactPoint[] | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  whatsapp?: string | null;
  occupation?: string | null;
  employer?: string | null;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  postalCode?: string | null;
  maritalStatus?: string | null;
  smoker?: boolean | null;
  preferredChannel?: string | null;
  source?: string | null;
  referredByPersonId?: string | null;
  referredByName?: string | null;
  householdId?: string | null;
  segment?: string | null;
  tags?: string[] | null;
  notes?: string | null;
  hot?: boolean | null;
  lastReviewDate?: string | null;
  nextReviewDate?: string | null;
  archivedAt?: string | null;
  /** v4: advisor who owns the relationship (team workspaces) */
  ownerId?: string | null;
  /** v7: the day this person became a client (set by the advisor, or when they first became a client) */
  clientSince?: string | null;
  /** v12: never call this person (honoured by the call screen, campaigns by phone and the prospect lists) */
  doNotCall?: boolean | null;
}
export const PersonFields = {
  ...base, ...ext,
  kind: { t: 'enum', req: true, values: PERSON_KINDS, index: true },
  stage: { t: 'enum', req: true, values: PERSON_STAGES, index: true },
  firstName: { t: 'text' }, lastName: { t: 'text' },
  displayName: { t: 'text', req: true, index: true },
  gender: { t: 'text' }, birthDate: { t: 'date' }, nameDay: { t: 'text' },
  phones: { t: 'json' }, emails: { t: 'json' },
  primaryPhone: { t: 'text', index: true }, primaryEmail: { t: 'text', index: true }, whatsapp: { t: 'text' },
  occupation: { t: 'text' }, employer: { t: 'text' }, address: { t: 'text' }, city: { t: 'text' },
  region: { t: 'text' }, country: { t: 'text' }, postalCode: { t: 'text' }, maritalStatus: { t: 'text' },
  smoker: { t: 'bool' }, preferredChannel: { t: 'text' }, source: { t: 'text' },
  referredByPersonId: { t: 'ref', ref: 'persons' }, referredByName: { t: 'text' },
  householdId: { t: 'ref', ref: 'households', index: true }, segment: { t: 'text' },
  tags: { t: 'json' }, notes: { t: 'text' }, hot: { t: 'bool' },
  lastReviewDate: { t: 'date' }, nextReviewDate: { t: 'date' }, archivedAt: { t: 'datetime' },
  ownerId: { t: 'text' }, clientSince: { t: 'date' }, doNotCall: { t: 'bool' },
} as const satisfies Fields<Person>;

export interface Household extends Base, Ext { name: string; notes?: string | null }
export const HouseholdFields = { ...base, ...ext, name: { t: 'text', req: true }, notes: { t: 'text' } } as const satisfies Fields<Household>;

export interface HouseholdMember extends Base {
  householdId: string; personId: string; role: HouseholdRole;
}
export const HouseholdMemberFields = {
  ...base,
  householdId: { t: 'ref', req: true, ref: 'households', index: true },
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  role: { t: 'enum', req: true, values: HOUSEHOLD_ROLES },
} as const satisfies Fields<HouseholdMember>;

export interface Policy extends Base, Ext {
  personId: string;
  insuredPersonId?: string | null;
  insuredName?: string | null;
  payerName?: string | null;
  beneficiaries?: string | null;
  branch: PolicyBranch;
  productName: string;
  carrier?: string | null;
  policyNumber?: string | null;
  status: PolicyStatus;
  startDate?: string | null;
  issueDate?: string | null;
  renewalDate?: string | null;
  endDate?: string | null;
  paymentFrequency?: PaymentFrequency | null;
  paymentMethod?: string | null;
  /** base annual premium, before adjustments (legacy baseAnnual) */
  annualPremium: number;
  currency?: string | null;
  sumAssured?: number | null;
  deductible?: number | null;
  commissionPct?: number | null;
  lastReviewDate?: string | null;
  nextReviewDate?: string | null;
  caseId?: string | null;
  notes?: string | null;
  // v4 (spec §99–§100)
  category?: PolicyCategory | null;
  parties?: PolicyParty[] | null;
  benefits?: PolicyBenefit[] | null;
  tags?: string[] | null;
  /** manual colour override (hex); otherwise category/insurer colour by configured precedence */
  color?: string | null;
  /** actual quoted instalment for the payment frequency (may include frequency loadings) */
  instalment?: number | null;
  replacesPolicyId?: string | null;
  // v13 — «Διαγραφή λανθασμένου συμβολαίου»: SOFT deletion of a wrongly entered policy (never a hard delete).
  // Set only by deletePolicy() with a server step-up grant; the sync server refuses any change of these three
  // fields without a valid single-use grant. A deleted policy is hidden from every list, report and statistic
  // (DatasetView.rows) together with the records that depend on it, and can be restored within the window.
  deletedAt?: string | null;
  /** account user id of the person who completed the deletion */
  deletedBy?: string | null;
  deleteReason?: string | null;
  // v14 — advisor's own catalogue (handoff v08 #14): Insurer → Product category → Specific plan. Optional links; the
  // text fields carrier / productName / category stay the denormalised display and history (a later rename or
  // deactivation in the catalogue never rewrites them unless the advisor chooses «update existing policies»).
  catalogInsurerId?: string | null;
  catalogCategoryId?: string | null;
  catalogPlanId?: string | null;
}
export const PolicyFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  insuredPersonId: { t: 'ref', ref: 'persons' }, insuredName: { t: 'text' }, payerName: { t: 'text' }, beneficiaries: { t: 'text' },
  branch: { t: 'enum', req: true, values: POLICY_BRANCHES },
  productName: { t: 'text', req: true },
  carrier: { t: 'text' }, policyNumber: { t: 'text', index: true },
  status: { t: 'enum', req: true, values: POLICY_STATUSES, index: true },
  startDate: { t: 'date' }, issueDate: { t: 'date' }, renewalDate: { t: 'date' }, endDate: { t: 'date' },
  paymentFrequency: { t: 'enum', values: PAYMENT_FREQUENCIES }, paymentMethod: { t: 'text' },
  annualPremium: { t: 'number', req: true }, currency: { t: 'text' },
  sumAssured: { t: 'number' }, deductible: { t: 'number' }, commissionPct: { t: 'number' },
  lastReviewDate: { t: 'date' }, nextReviewDate: { t: 'date' },
  caseId: { t: 'ref', ref: 'cases' }, notes: { t: 'text' },
  category: { t: 'enum', values: POLICY_CATEGORIES }, parties: { t: 'json' }, benefits: { t: 'json' }, tags: { t: 'json' },
  color: { t: 'text' }, instalment: { t: 'number' }, replacesPolicyId: { t: 'ref', ref: 'policies' },
  deletedAt: { t: 'datetime' }, deletedBy: { t: 'text' }, deleteReason: { t: 'text' },
  catalogInsurerId: { t: 'ref', ref: 'catalog_insurers', index: true }, catalogCategoryId: { t: 'ref', ref: 'catalog_categories' }, catalogPlanId: { t: 'ref', ref: 'catalog_plans' },
} as const satisfies Fields<Policy>;

/** Premium adjustment. `date` = effective date; `annualAmount` = signed annual delta. v4 adds the full before/after record. */
export interface PolicyAdjustment extends Base, Ext {
  policyId: string; date?: string | null; type: string; annualAmount: number; note?: string | null;
  previousAnnual?: number | null; newAnnual?: number | null; reason?: string | null; source?: string | null;
  /** a reversal points at the adjustment it cancels (history is never deleted) */
  reversesId?: string | null; actor?: string | null;
}
export const PolicyAdjustmentFields = {
  ...base, ...ext,
  policyId: { t: 'ref', req: true, ref: 'policies', index: true },
  date: { t: 'date' }, type: { t: 'text', req: true }, annualAmount: { t: 'number', req: true }, note: { t: 'text' },
  previousAnnual: { t: 'number' }, newAnnual: { t: 'number' }, reason: { t: 'text' }, source: { t: 'text' },
  reversesId: { t: 'ref', ref: 'policy_adjustments' }, actor: { t: 'text' },
} as const satisfies Fields<PolicyAdjustment>;

export interface Case extends Base, Ext {
  personId: string;
  product: string;
  insurer?: string | null;
  applicationNumber?: string | null;
  stage: CaseStage;
  priority: Priority;
  premium?: number | null;
  sumAssured?: number | null;
  submittedAt?: string | null;
  targetDate?: string | null;
  nextAction?: string | null;
  nextActionDate?: string | null;
  nextActionTime?: string | null;
  underwritingStatus?: string | null;
  policyId?: string | null;
  closedAt?: string | null;
  notes?: string | null;
  /** v5: assigned advisor (platform user id), product category for checklists, last reopening */
  advisorId?: string | null; category?: string | null; reopenedAt?: string | null;
}
export const CaseFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  product: { t: 'text', req: true }, insurer: { t: 'text' }, applicationNumber: { t: 'text' },
  stage: { t: 'enum', req: true, values: CASE_STAGES, index: true },
  priority: { t: 'enum', req: true, values: PRIORITIES },
  premium: { t: 'number' }, sumAssured: { t: 'number' },
  submittedAt: { t: 'date' }, targetDate: { t: 'date' },
  nextAction: { t: 'text' }, nextActionDate: { t: 'date' }, nextActionTime: { t: 'time' },
  underwritingStatus: { t: 'text' }, policyId: { t: 'ref', ref: 'policies' }, closedAt: { t: 'datetime' }, notes: { t: 'text' },
  advisorId: { t: 'text', index: true }, category: { t: 'text' }, reopenedAt: { t: 'datetime' },
} as const satisfies Fields<Case>;

export interface CaseRequirement extends Base {
  caseId: string; category: RequirementCategory; label?: string | null; done: boolean; doneAt?: string | null; note?: string | null; sort: number;
  /** v5: lifecycle (done stays the source of truth for older readers: received/waived ⇒ done) */
  status?: RequirementStatus | null; waitingOn?: WaitingOn | null; requestedAt?: string | null; receivedAt?: string | null;
  expiresAt?: string | null; documentId?: string | null; templateId?: string | null; mandatory?: boolean | null;
}
export const CaseRequirementFields = {
  ...base,
  caseId: { t: 'ref', req: true, ref: 'cases', index: true },
  category: { t: 'enum', req: true, values: REQUIREMENT_CATEGORIES },
  label: { t: 'text' }, done: { t: 'bool', req: true }, doneAt: { t: 'datetime' }, note: { t: 'text' }, sort: { t: 'int', req: true },
  status: { t: 'enum', values: REQUIREMENT_STATUSES }, waitingOn: { t: 'enum', values: WAITING_ON }, requestedAt: { t: 'date' }, receivedAt: { t: 'date' },
  expiresAt: { t: 'date' }, documentId: { t: 'ref', ref: 'documents' }, templateId: { t: 'text' }, mandatory: { t: 'bool' },
} as const satisfies Fields<CaseRequirement>;

export interface CaseEvent extends Base { caseId: string; at: string; stage: CaseStage; note?: string | null }
export const CaseEventFields = {
  ...base,
  caseId: { t: 'ref', req: true, ref: 'cases', index: true },
  at: { t: 'datetime', req: true }, stage: { t: 'enum', req: true, values: CASE_STAGES }, note: { t: 'text' },
} as const satisfies Fields<CaseEvent>;

export interface Task extends Base, Ext {
  kind: TaskKind;
  title: string;
  personId?: string | null;
  prospectId?: string | null;
  caseId?: string | null;
  policyId?: string | null;
  dueDate?: string | null;
  dueTime?: string | null;
  durationMin?: number | null;
  locationType?: LocationType | null;
  location?: string | null;
  status: TaskStatus;
  priority: Priority;
  outcome?: Outcome | null;
  notes?: string | null;
  completedAt?: string | null;
  /** what created the task: manual | case | review | note | legacy | prospect */
  origin?: string | null;
  /** v4: first due date, kept when a task is moved (evening review) */
  originalDueDate?: string | null;
  rescheduleCount?: number | null;
}
export const TaskFields = {
  ...base, ...ext,
  kind: { t: 'enum', req: true, values: TASK_KINDS },
  title: { t: 'text', req: true },
  personId: { t: 'ref', ref: 'persons', index: true }, prospectId: { t: 'ref', ref: 'prospects' },
  caseId: { t: 'ref', ref: 'cases', index: true }, policyId: { t: 'ref', ref: 'policies' },
  dueDate: { t: 'date', index: true }, dueTime: { t: 'time' }, durationMin: { t: 'int' },
  locationType: { t: 'enum', values: LOCATION_TYPES }, location: { t: 'text' },
  status: { t: 'enum', req: true, values: TASK_STATUSES, index: true },
  priority: { t: 'enum', req: true, values: PRIORITIES },
  outcome: { t: 'enum', values: OUTCOMES }, notes: { t: 'text' }, completedAt: { t: 'datetime' }, origin: { t: 'text' },
  originalDueDate: { t: 'date' }, rescheduleCount: { t: 'int' },
} as const satisfies Fields<Task>;

export interface Note extends Base, Ext {
  personId?: string | null; prospectId?: string | null; title?: string | null; body: string; pinned?: boolean | null; taskId?: string | null;
}
export const NoteFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true }, prospectId: { t: 'ref', ref: 'prospects' },
  title: { t: 'text' }, body: { t: 'text', req: true }, pinned: { t: 'bool' }, taskId: { t: 'ref', ref: 'tasks' },
} as const satisfies Fields<Note>;

export interface Review extends Base, Ext {
  personId: string; date: string; nextReviewDate?: string | null; outcome: ReviewOutcome; notes?: string | null;
  /** v5: review questions with the client's answers, and the annual portfolio at the time (evidence) */
  answers?: unknown; portfolioAnnual?: number | null;
}
export const ReviewFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  date: { t: 'date', req: true }, nextReviewDate: { t: 'date' },
  outcome: { t: 'enum', req: true, values: REVIEW_OUTCOMES }, notes: { t: 'text' },
  answers: { t: 'json' }, portfolioAnnual: { t: 'number' },
} as const satisfies Fields<Review>;

export interface TimelineEvent extends Base, Ext {
  personId?: string | null; at: string; type: TimelineType; title: string; body?: string | null; refTable?: string | null; refId?: string | null;
}
export const TimelineEventFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true },
  at: { t: 'datetime', req: true, index: true }, type: { t: 'enum', req: true, values: TIMELINE_TYPES },
  title: { t: 'text', req: true }, body: { t: 'text' }, refTable: { t: 'text' }, refId: { t: 'text' },
} as const satisfies Fields<TimelineEvent>;

export interface ProspectList extends Base, Ext {
  name: string; description?: string | null; source?: string | null; campaign?: string | null; archived?: boolean | null;
  /** v12: opening lines per variant ({ A: { title, opening, notes }, B: … }) and the default lawful basis / consent source of the list */
  scripts?: Record<string, CallScript> | null; lawfulBasis?: string | null; consentSource?: string | null;
}
/** v12: an opening line of a call list (A/B variants) */
export interface CallScript { title?: string | null; opening?: string | null; notes?: string | null }
export const ProspectListFields = {
  ...base, ...ext,
  name: { t: 'text', req: true }, description: { t: 'text' }, source: { t: 'text' }, campaign: { t: 'text' }, archived: { t: 'bool' },
  scripts: { t: 'json' }, lawfulBasis: { t: 'text' }, consentSource: { t: 'text' },
} as const satisfies Fields<ProspectList>;

export interface Prospect extends Base, Ext {
  listId: string; name: string; phone?: string | null; email?: string | null; profession?: string | null; company?: string | null;
  area?: string | null; source?: string | null; priority: Priority; status: ProspectStatus; notes?: string | null;
  lastCallDate?: string | null; nextFollowUpDate?: string | null; appointmentDate?: string | null; convertedPersonId?: string | null;
  /** v12: never call again; on what basis the number is held and where it came from; the variant assigned; dials so far */
  doNotCall?: boolean | null; lawfulBasis?: string | null; consentSource?: string | null; consentDate?: string | null;
  scriptVariant?: string | null; attempts?: number | null;
}
export const ProspectFields = {
  ...base, ...ext,
  listId: { t: 'ref', req: true, ref: 'prospect_lists', index: true },
  name: { t: 'text', req: true }, phone: { t: 'text', index: true }, email: { t: 'text' }, profession: { t: 'text' }, company: { t: 'text' },
  area: { t: 'text' }, source: { t: 'text' }, priority: { t: 'enum', req: true, values: PRIORITIES },
  status: { t: 'enum', req: true, values: PROSPECT_STATUSES, index: true }, notes: { t: 'text' },
  lastCallDate: { t: 'date' }, nextFollowUpDate: { t: 'date' }, appointmentDate: { t: 'date' }, convertedPersonId: { t: 'ref', ref: 'persons' },
  doNotCall: { t: 'bool' }, lawfulBasis: { t: 'text' }, consentSource: { t: 'text' }, consentDate: { t: 'date' }, scriptVariant: { t: 'text' }, attempts: { t: 'int' },
} as const satisfies Fields<Prospect>;

export interface DocumentRecord extends Base, Ext {
  personId?: string | null; policyId?: string | null; caseId?: string | null; title: string; type?: string | null;
  reference?: string | null; url?: string | null; date?: string | null; notes?: string | null;
  // v4 document vault (spec §31, §102)
  category?: DocCategory | null; fileId?: string | null; filename?: string | null; mime?: string | null; size?: number | null;
  sha256?: string | null; docVersion?: number | null; versionOf?: string | null; source?: string | null; status?: string | null;
  restricted?: boolean | null; claimId?: string | null; quotationId?: string | null; meetingId?: string | null; expiryDate?: string | null;
  /** v18: a file of the advisor's OWN tax folder (tax_years.id) — private to the member who stored it (see tax-folder.ts);
   *  never shown in the client vault, never sent to apps older than 18 */
  taxYearId?: string | null;
}
export const DocumentFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true }, policyId: { t: 'ref', ref: 'policies' }, caseId: { t: 'ref', ref: 'cases' },
  title: { t: 'text', req: true }, type: { t: 'text' }, reference: { t: 'text' }, url: { t: 'text' }, date: { t: 'date' }, notes: { t: 'text' },
  category: { t: 'enum', values: DOC_CATEGORIES }, fileId: { t: 'text' }, filename: { t: 'text' }, mime: { t: 'text' }, size: { t: 'int' },
  sha256: { t: 'text' }, docVersion: { t: 'int' }, versionOf: { t: 'ref', ref: 'documents' }, source: { t: 'text' }, status: { t: 'text' },
  restricted: { t: 'bool' }, claimId: { t: 'ref', ref: 'claims' }, quotationId: { t: 'ref', ref: 'quotations' }, meetingId: { t: 'ref', ref: 'meetings' }, expiryDate: { t: 'date' },
  taxYearId: { t: 'ref', ref: 'tax_years', index: true },
} as const satisfies Fields<DocumentRecord>;

export interface ServiceCase extends Base, Ext {
  personId: string; policyId?: string | null; title: string; status: ServiceStatus; nextAction?: string | null; notes?: string | null;
}
export const ServiceCaseFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, policyId: { t: 'ref', ref: 'policies' },
  title: { t: 'text', req: true }, status: { t: 'enum', req: true, values: SERVICE_STATUSES }, nextAction: { t: 'text' }, notes: { t: 'text' },
} as const satisfies Fields<ServiceCase>;

export interface Consent extends Base, Ext {
  personId: string; purpose: string; granted: boolean; date?: string | null; channel?: string | null; notes?: string | null;
  /** v4: lawful basis (consent, contract, legal_obligation, legitimate_interest), scope, source, withdrawal */
  lawfulBasis?: string | null; scope?: string | null; source?: string | null; withdrawnAt?: string | null;
}
export const ConsentFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  purpose: { t: 'text', req: true }, granted: { t: 'bool', req: true }, date: { t: 'date' }, channel: { t: 'text' }, notes: { t: 'text' },
  lawfulBasis: { t: 'text' }, scope: { t: 'text' }, source: { t: 'text' }, withdrawnAt: { t: 'datetime' },
} as const satisfies Fields<Consent>;

/**
 * Lossless container for legacy collections whose dedicated module is not rebuilt yet
 * (comparisons, meetings, needs analyses, tax/risk profiles, quotes, proposals, ...).
 */
export interface LegacyRecord extends Base {
  personId?: string | null; kind: string; title?: string | null; date?: string | null; payload: unknown; sourceVersion?: string | null;
}
export const LegacyRecordFields = {
  ...base,
  personId: { t: 'ref', ref: 'persons', index: true },
  kind: { t: 'text', req: true, index: true }, title: { t: 'text' }, date: { t: 'date' },
  payload: { t: 'json', req: true }, sourceVersion: { t: 'text' },
} as const satisfies Fields<LegacyRecord>;

/** Policy Comparator (schema v2). `plans` holds the inputs; `snapshot` freezes inputs + results at decision time. */
export interface Comparison extends Base, Ext {
  personId: string;
  title: string;
  status: ComparisonStatus;
  plans: unknown;
  decision?: ComparisonDecision | null;
  decidedAt?: string | null;
  snapshot?: unknown;
  notes?: string | null;
  sourceComparisonId?: string | null;
}
export const ComparisonFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true },
  title: { t: 'text', req: true }, status: { t: 'enum', req: true, values: COMPARISON_STATUSES },
  plans: { t: 'json', req: true }, decision: { t: 'enum', values: COMPARISON_DECISIONS }, decidedAt: { t: 'datetime' },
  snapshot: { t: 'json' }, notes: { t: 'text' }, sourceComparisonId: { t: 'ref', ref: 'comparisons' },
} as const satisfies Fields<Comparison>;

// ---------------------------------------------------------------------------
// schema v3 entities

/** Needs Analysis + First Profile + Risk profile, versioned: a final analysis is never edited, a new version is created. */
export interface NeedsAnalysis extends Base, Ext {
  personId: string; date: string; status: NeedsStatus; revision: number;
  /** discovery profile: income, liabilities, dependants, goals, concerns, savings, retirement age, smoker, ... */
  profile: unknown;
  /** per area: { area, target, existing, priority 0-3, reasoning, action } */
  areas: unknown;
  riskAttitude?: RiskAttitude | null; notes?: string | null; sourceNeedsId?: string | null; finalizedAt?: string | null;
}
export const NeedsAnalysisFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, date: { t: 'date', req: true },
  status: { t: 'enum', req: true, values: NEEDS_STATUSES }, revision: { t: 'int', req: true }, profile: { t: 'json', req: true }, areas: { t: 'json', req: true },
  riskAttitude: { t: 'enum', values: RISK_ATTITUDES }, notes: { t: 'text' }, sourceNeedsId: { t: 'ref', ref: 'needs_analyses' }, finalizedAt: { t: 'datetime' },
} as const satisfies Fields<NeedsAnalysis>;

export interface Quotation extends Base, Ext {
  personId: string; insurer?: string | null; product: string; quoteNumber?: string | null; date?: string | null;
  premium?: number | null; frequency?: PaymentFrequency | null; sumInsured?: number | null; status: QuoteStatus;
  expiryDate?: string | null; comparisonId?: string | null; caseId?: string | null; url?: string | null; notes?: string | null;
}
export const QuotationFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, insurer: { t: 'text' }, product: { t: 'text', req: true },
  quoteNumber: { t: 'text', index: true }, date: { t: 'date' }, premium: { t: 'number' }, frequency: { t: 'enum', values: PAYMENT_FREQUENCIES },
  sumInsured: { t: 'number' }, status: { t: 'enum', req: true, values: QUOTE_STATUSES }, expiryDate: { t: 'date', index: true },
  comparisonId: { t: 'ref', ref: 'comparisons' }, caseId: { t: 'ref', ref: 'cases' }, url: { t: 'text' }, notes: { t: 'text' },
} as const satisfies Fields<Quotation>;

export interface Opportunity extends Base, Ext {
  personId?: string | null; prospectId?: string | null; title: string; stage: OpportunityStage; expectedPremium?: number | null;
  targetClose?: string | null; source?: string | null; reason?: string | null; objections?: string | null; nextActionDate?: string | null;
  outcome?: string | null; closedAt?: string | null; comparisonId?: string | null; quotationId?: string | null;
}
export const OpportunityFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true }, prospectId: { t: 'ref', ref: 'prospects' }, title: { t: 'text', req: true },
  stage: { t: 'enum', req: true, values: OPPORTUNITY_STAGES, index: true }, expectedPremium: { t: 'number' }, targetClose: { t: 'date' },
  source: { t: 'text' }, reason: { t: 'text' }, objections: { t: 'text' }, nextActionDate: { t: 'date' }, outcome: { t: 'text' }, closedAt: { t: 'datetime' },
  comparisonId: { t: 'ref', ref: 'comparisons' }, quotationId: { t: 'ref', ref: 'quotations' },
} as const satisfies Fields<Opportunity>;

export interface Referral extends Base, Ext {
  referrerId: string; name: string; phone?: string | null; email?: string | null; status: ReferralStatus; date: string;
  personId?: string | null; thankedAt?: string | null; notes?: string | null;
  /** v5: how they are related, contact attempts, appointment and next step */
  relationship?: string | null; attempts?: unknown; appointmentAt?: string | null; nextActionDate?: string | null;
}
export const ReferralFields = {
  ...base, ...ext,
  referrerId: { t: 'ref', req: true, ref: 'persons', index: true }, name: { t: 'text', req: true }, phone: { t: 'text' }, email: { t: 'text' },
  status: { t: 'enum', req: true, values: REFERRAL_STATUSES }, date: { t: 'date', req: true }, personId: { t: 'ref', ref: 'persons' },
  thankedAt: { t: 'datetime' }, notes: { t: 'text' },
  relationship: { t: 'text' }, attempts: { t: 'json' }, appointmentAt: { t: 'datetime' }, nextActionDate: { t: 'date' },
} as const satisfies Fields<Referral>;

export interface Communication extends Base, Ext {
  personId?: string | null; prospectId?: string | null; at: string; channel: CommChannel; direction: CommDirection;
  subject?: string | null; summary?: string | null; outcome?: Outcome | null; taskId?: string | null;
}
export const CommunicationFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true }, prospectId: { t: 'ref', ref: 'prospects' }, at: { t: 'datetime', req: true, index: true },
  channel: { t: 'enum', req: true, values: COMM_CHANNELS }, direction: { t: 'enum', req: true, values: COMM_DIRECTIONS },
  subject: { t: 'text' }, summary: { t: 'text' }, outcome: { t: 'enum', values: OUTCOMES }, taskId: { t: 'ref', ref: 'tasks' },
} as const satisfies Fields<Communication>;

/** Meeting record; `commitments` = [{ id, by: 'advisor' | 'client', text, dueDate, taskId, done }] (spec §1 "what did I promise / what did they promise"). */
export interface Meeting extends Base, Ext {
  personId: string; at: string; status: MeetingStatus; purpose?: string | null; attendees?: string | null; agenda?: string | null;
  notes?: string | null; decisions?: string | null; objections?: string | null; opportunities?: string | null; commitments: unknown; taskId?: string | null;
}
export const MeetingFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, at: { t: 'datetime', req: true, index: true },
  status: { t: 'enum', req: true, values: MEETING_STATUSES }, purpose: { t: 'text' }, attendees: { t: 'text' }, agenda: { t: 'text' },
  notes: { t: 'text' }, decisions: { t: 'text' }, objections: { t: 'text' }, opportunities: { t: 'text' }, commitments: { t: 'json', req: true },
  taskId: { t: 'ref', ref: 'tasks' },
} as const satisfies Fields<Meeting>;

export interface Claim extends Base, Ext {
  personId: string; policyId?: string | null; type: string; eventDate?: string | null; insurer?: string | null; status: ClaimStatus;
  reference?: string | null; amountClaimed?: number | null; amountPaid?: number | null; nextAction?: string | null; nextActionDate?: string | null;
  requirements?: unknown; notes?: string | null; closedAt?: string | null;
}
export const ClaimFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, policyId: { t: 'ref', ref: 'policies' }, type: { t: 'text', req: true },
  eventDate: { t: 'date' }, insurer: { t: 'text' }, status: { t: 'enum', req: true, values: CLAIM_STATUSES }, reference: { t: 'text' },
  amountClaimed: { t: 'number' }, amountPaid: { t: 'number' }, nextAction: { t: 'text' }, nextActionDate: { t: 'date' },
  requirements: { t: 'json' }, notes: { t: 'text' }, closedAt: { t: 'datetime' },
} as const satisfies Fields<Claim>;

/** User overrides of the multilingual default templates (defaults live in i18n under template.*). */
export interface Template extends Base { key: string; locale: string; channel?: string | null; subject?: string | null; body: string }
export const TemplateFields = {
  ...base, key: { t: 'text', req: true, index: true }, locale: { t: 'text', req: true }, channel: { t: 'text' }, subject: { t: 'text' }, body: { t: 'text', req: true },
} as const satisfies Fields<Template>;

export interface Setting extends Base { key: string; value: unknown }
export const SettingFields = { ...base, key: { t: 'text', req: true, index: true }, value: { t: 'json' } } as const satisfies Fields<Setting>;

export interface AuditEvent extends Base {
  at: string; actor: string; action: string; entity?: string | null; entityId?: string | null; summary?: string | null; data?: unknown;
}
export const AuditEventFields = {
  ...base,
  at: { t: 'datetime', req: true, index: true }, actor: { t: 'text', req: true }, action: { t: 'text', req: true },
  entity: { t: 'text' }, entityId: { t: 'text' }, summary: { t: 'text' }, data: { t: 'json' },
} as const satisfies Fields<AuditEvent>;

// ---------------------------------------------------------------------------
// schema v4 entities (spec §98–§115)

/** Production ledger — auditable, distinct from the portfolio and from collected cash (spec §101). */
export interface ProductionEvent extends Base, Ext {
  personId?: string | null; policyId?: string | null; type: ProductionType; date: string; effectiveDate?: string | null;
  /** signed annualised delta credited by this event (major units, 2 decimals) */
  annualDelta: number; basis: string; currency: string; branch?: string | null; category?: string | null;
  /** sales context tags (cross_sale, upsell, campaign) — never counted twice */
  contexts?: string[] | null; sourceTable?: string | null; sourceId?: string | null;
  /** idempotency key: the same business event can never be credited twice */
  key: string; reversesId?: string | null; campaignId?: string | null; note?: string | null;
}
export const ProductionEventFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', index: true }, policyId: { t: 'ref', ref: 'policies', index: true },
  type: { t: 'enum', req: true, values: PRODUCTION_TYPES }, date: { t: 'date', req: true, index: true }, effectiveDate: { t: 'date' },
  annualDelta: { t: 'number', req: true }, basis: { t: 'text', req: true }, currency: { t: 'text', req: true }, branch: { t: 'text' }, category: { t: 'text' },
  contexts: { t: 'json' }, sourceTable: { t: 'text' }, sourceId: { t: 'text' }, key: { t: 'text', req: true, index: true },
  reversesId: { t: 'ref', ref: 'production_events' }, campaignId: { t: 'ref', ref: 'campaigns' }, note: { t: 'text' },
} as const satisfies Fields<ProductionEvent>;

export interface Renewal extends Base, Ext {
  policyId: string; personId: string; dueDate: string; lastPremium?: number | null; renewalPremium?: number | null; currency?: string | null;
  status: RenewalStatus; quotationId?: string | null; remindedAt?: string | null; decidedAt?: string | null; notes?: string | null;
  /** v8: general-insurance renewal pipeline stage and when it was last moved (optional; older rows have none) */
  stage?: GiRenewalStage | null; stageAt?: string | null;
}
export const RenewalFields = {
  ...base, ...ext,
  policyId: { t: 'ref', req: true, ref: 'policies', index: true }, personId: { t: 'ref', req: true, ref: 'persons', index: true },
  dueDate: { t: 'date', req: true, index: true }, lastPremium: { t: 'number' }, renewalPremium: { t: 'number' }, currency: { t: 'text' },
  status: { t: 'enum', req: true, values: RENEWAL_STATUSES }, quotationId: { t: 'ref', ref: 'quotations' }, remindedAt: { t: 'datetime' },
  decidedAt: { t: 'datetime' }, notes: { t: 'text' },
  stage: { t: 'enum', values: GI_RENEWAL_STAGES }, stageAt: { t: 'datetime' },
} as const satisfies Fields<Renewal>;

export interface CommissionRule extends Base, Ext {
  name: string; insurer?: string | null; category?: string | null; kind: CommissionKind; ratePct: number; basis: string;
  fromDate?: string | null; toDate?: string | null; active: boolean; source?: string | null; notes?: string | null;
}
export const CommissionRuleFields = {
  ...base, ...ext,
  name: { t: 'text', req: true }, insurer: { t: 'text' }, category: { t: 'text' }, kind: { t: 'enum', req: true, values: COMMISSION_KINDS },
  ratePct: { t: 'number', req: true }, basis: { t: 'text', req: true }, fromDate: { t: 'date' }, toDate: { t: 'date' }, active: { t: 'bool', req: true },
  source: { t: 'text' }, notes: { t: 'text' },
} as const satisfies Fields<CommissionRule>;

/** Commission line. Stores the rate it was computed with, so later rule edits never rewrite a statement. */
export interface CommissionEntry extends Base, Ext {
  policyId?: string | null; personId?: string | null; productionEventId?: string | null; ruleId?: string | null; kind: CommissionKind;
  period: string; ratePct?: number | null; expectedAmount: number; paidAmount?: number | null; currency: string; status: CommissionStatus;
  statementRef?: string | null; paidDate?: string | null; key: string; notes?: string | null;
}
export const CommissionEntryFields = {
  ...base, ...ext,
  policyId: { t: 'ref', ref: 'policies', index: true }, personId: { t: 'ref', ref: 'persons' }, productionEventId: { t: 'ref', ref: 'production_events' },
  ruleId: { t: 'ref', ref: 'commission_rules' }, kind: { t: 'enum', req: true, values: COMMISSION_KINDS }, period: { t: 'text', req: true, index: true },
  ratePct: { t: 'number' }, expectedAmount: { t: 'number', req: true }, paidAmount: { t: 'number' }, currency: { t: 'text', req: true },
  status: { t: 'enum', req: true, values: COMMISSION_STATUSES }, statementRef: { t: 'text' }, paidDate: { t: 'date' }, key: { t: 'text', req: true, index: true }, notes: { t: 'text' },
} as const satisfies Fields<CommissionEntry>;

/** Admin-supplied or updated country/tax-year rules (built-in packs live in code; these override/extend them). */
export interface TaxRulesetRow extends Base, Ext {
  rulesetId: string; region: string; taxYear: number; rulesetVersion: number; status: TaxRuleStatus; rules: unknown;
  source?: string | null; verifiedAt?: string | null; effectiveFrom?: string | null; effectiveTo?: string | null; approvedBy?: string | null; notes?: string | null;
}
export const TaxRulesetFields = {
  ...base, ...ext,
  rulesetId: { t: 'text', req: true, index: true }, region: { t: 'text', req: true }, taxYear: { t: 'int', req: true }, rulesetVersion: { t: 'int', req: true },
  status: { t: 'enum', req: true, values: TAX_RULE_STATUSES }, rules: { t: 'json', req: true }, source: { t: 'text' }, verifiedAt: { t: 'date' },
  effectiveFrom: { t: 'date' }, effectiveTo: { t: 'date' }, approvedBy: { t: 'text' }, notes: { t: 'text' },
} as const satisfies Fields<TaxRulesetRow>;

/** A saved tax calculation. It keeps the ruleset id + version it used, forever (spec §103). */
export interface TaxSnapshot extends Base, Ext {
  personId: string; region: string; taxYear: number; rulesetId: string; rulesetVersion: number; status: string;
  inputs: unknown; result: unknown; date: string; needsId?: string | null; notes?: string | null;
}
export const TaxSnapshotFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, region: { t: 'text', req: true }, taxYear: { t: 'int', req: true },
  rulesetId: { t: 'text', req: true }, rulesetVersion: { t: 'int', req: true }, status: { t: 'text', req: true },
  inputs: { t: 'json', req: true }, result: { t: 'json', req: true }, date: { t: 'date', req: true }, needsId: { t: 'ref', ref: 'needs_analyses' }, notes: { t: 'text' },
} as const satisfies Fields<TaxSnapshot>;

/** Income, expense, liability, goal or asset (IncomeRecord / ExpenseRecord / Liability / FinancialGoal in one table). null amount = unknown. */
export interface FinancialItem extends Base, Ext {
  personId: string; kind: FinancialKind; type: string; label?: string | null; amount?: number | null; frequency?: string | null; currency?: string | null;
  balance?: number | null; ratePct?: number | null; termMonths?: number | null; startDate?: string | null; endDate?: string | null; lender?: string | null;
  gross?: boolean | null; source?: string | null; sourceDocumentId?: string | null; notes?: string | null;
}
export const FinancialItemFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, kind: { t: 'enum', req: true, values: FINANCIAL_KINDS }, type: { t: 'text', req: true },
  label: { t: 'text' }, amount: { t: 'number' }, frequency: { t: 'text' }, currency: { t: 'text' }, balance: { t: 'number' }, ratePct: { t: 'number' },
  termMonths: { t: 'int' }, startDate: { t: 'date' }, endDate: { t: 'date' }, lender: { t: 'text' }, gross: { t: 'bool' }, source: { t: 'text' },
  sourceDocumentId: { t: 'ref', ref: 'documents' }, notes: { t: 'text' },
} as const satisfies Fields<FinancialItem>;

export interface LifeEvent extends Base, Ext {
  personId: string; type: LifeEventType; date: string; notes?: string | null; relatedPersonId?: string | null; taskId?: string | null; processedAt?: string | null;
}
export const LifeEventFields = {
  ...base, ...ext,
  personId: { t: 'ref', req: true, ref: 'persons', index: true }, type: { t: 'enum', req: true, values: LIFE_EVENT_TYPES }, date: { t: 'date', req: true },
  notes: { t: 'text' }, relatedPersonId: { t: 'ref', ref: 'persons' }, taskId: { t: 'ref', ref: 'tasks' }, processedAt: { t: 'datetime' },
} as const satisfies Fields<LifeEvent>;

/** Campaign. `audience` is the frozen, consent-checked snapshot taken when the campaign was prepared. Nothing is ever sent by AVRASTRO itself. */
export interface Campaign extends Base, Ext {
  title: string; theme?: string | null; status: CampaignStatus; segmentKey?: string | null; criteria?: unknown; audience?: unknown;
  channel?: string | null; locale?: string | null; message?: unknown; launchedAt?: string | null; completedAt?: string | null; notes?: string | null;
  /** v5: what the campaign is for (appointments, reviews, new business, reactivation, referrals…) */
  objective?: string | null;
}
export const CampaignFields = {
  ...base, ...ext,
  title: { t: 'text', req: true }, theme: { t: 'text' }, status: { t: 'enum', req: true, values: CAMPAIGN_STATUSES }, segmentKey: { t: 'text' },
  criteria: { t: 'json' }, audience: { t: 'json' }, channel: { t: 'text' }, locale: { t: 'text' }, message: { t: 'json' },
  launchedAt: { t: 'datetime' }, completedAt: { t: 'datetime' }, notes: { t: 'text' }, objective: { t: 'text' },
} as const satisfies Fields<Campaign>;

export interface CampaignActivity extends Base, Ext {
  campaignId: string; personId?: string | null; type: CampaignActivityType; at: string; refTable?: string | null; refId?: string | null;
  amount?: number | null; key: string; note?: string | null;
}
export const CampaignActivityFields = {
  ...base, ...ext,
  campaignId: { t: 'ref', req: true, ref: 'campaigns', index: true }, personId: { t: 'ref', ref: 'persons', index: true },
  type: { t: 'enum', req: true, values: CAMPAIGN_ACTIVITY_TYPES }, at: { t: 'datetime', req: true }, refTable: { t: 'text' }, refId: { t: 'text' },
  amount: { t: 'number' }, key: { t: 'text', req: true, index: true }, note: { t: 'text' },
} as const satisfies Fields<CampaignActivity>;

/** WHEN/THEN rule. `ruleVersion` increases on every edit; runs record the version they executed. */
export interface AutomationRule extends Base, Ext {
  name: string; trigger: unknown; action: unknown; enabled: boolean; ruleVersion: number; lastRunAt?: string | null; notes?: string | null;
}
export const AutomationRuleFields = {
  ...base, ...ext,
  name: { t: 'text', req: true }, trigger: { t: 'json', req: true }, action: { t: 'json', req: true }, enabled: { t: 'bool', req: true },
  ruleVersion: { t: 'int', req: true }, lastRunAt: { t: 'datetime' }, notes: { t: 'text' },
} as const satisfies Fields<AutomationRule>;

export interface AutomationRun extends Base, Ext {
  ruleId: string; ruleVersion: number; key: string; at: string; result: string; personId?: string | null; refTable?: string | null; refId?: string | null;
}
export const AutomationRunFields = {
  ...base, ...ext,
  ruleId: { t: 'ref', req: true, ref: 'automation_rules', index: true }, ruleVersion: { t: 'int', req: true }, key: { t: 'text', req: true, index: true },
  at: { t: 'datetime', req: true }, result: { t: 'text', req: true }, personId: { t: 'ref', ref: 'persons' }, refTable: { t: 'text' }, refId: { t: 'text' },
} as const satisfies Fields<AutomationRun>;

/** Extracted values waiting for the advisor's review. Nothing reaches CRM records until each value is accepted. */
export interface ExtractionDraft extends Base, Ext {
  documentId: string; personId?: string | null; status: ExtractionStatus; docType?: string | null; method: string; fields: unknown;
  error?: string | null; reviewedAt?: string | null; appliedAt?: string | null;
}
export const ExtractionDraftFields = {
  ...base, ...ext,
  documentId: { t: 'ref', req: true, ref: 'documents', index: true }, personId: { t: 'ref', ref: 'persons', index: true },
  status: { t: 'enum', req: true, values: EXTRACTION_STATUSES }, docType: { t: 'text' }, method: { t: 'text', req: true }, fields: { t: 'json', req: true },
  error: { t: 'text' }, reviewedAt: { t: 'datetime' }, appliedAt: { t: 'datetime' },
} as const satisfies Fields<ExtractionDraft>;

/** Admin custom field definition; values live in `row.data.custom[key]` of the entity. */
export interface CustomField extends Base {
  entity: string; key: string; type: CustomFieldType; labels: unknown; options?: unknown; required?: boolean | null; active: boolean; sort: number;
}
export const CustomFieldFields = {
  ...base,
  entity: { t: 'text', req: true, index: true }, key: { t: 'text', req: true }, type: { t: 'enum', req: true, values: CUSTOM_FIELD_TYPES },
  labels: { t: 'json', req: true }, options: { t: 'json' }, required: { t: 'bool' }, active: { t: 'bool', req: true }, sort: { t: 'int', req: true },
} as const satisfies Fields<CustomField>;

/** Durable sync outbox + applied log (device-local; excluded from backups). id = operation id. */
export interface SyncOp extends Base {
  csId: string; table: string; rowId: string; kind: string; baseVersion: number; rowVersion: number; fields?: unknown; row?: unknown;
  at: string; deviceId: string; state: SyncOpState; seq?: number | null; error?: string | null;
  /** v13: signed single-use step-up grant sent with a policy deletion/restore (device-local, never replicated as data) */
  stepUpGrant?: string | null;
}
export const SyncOpFields = {
  ...base,
  csId: { t: 'text', req: true, index: true }, table: { t: 'text', req: true }, rowId: { t: 'text', req: true, index: true }, kind: { t: 'text', req: true },
  baseVersion: { t: 'int', req: true }, rowVersion: { t: 'int', req: true }, fields: { t: 'json' }, row: { t: 'json' }, at: { t: 'datetime', req: true },
  deviceId: { t: 'text', req: true }, state: { t: 'enum', req: true, values: SYNC_OP_STATES, index: true }, seq: { t: 'int' }, error: { t: 'text' },
  stepUpGrant: { t: 'text' },
} as const satisfies Fields<SyncOp>;

/** Local vs cloud version waiting for the advisor (never silently last-write-wins). */
export interface SyncConflict extends Base {
  table: string; rowId: string; localRow?: unknown; remoteRow?: unknown; fields: unknown; status: SyncConflictStatus; reason: string;
  resolution?: string | null; resolvedAt?: string | null; opId?: string | null;
}
export const SyncConflictFields = {
  ...base,
  table: { t: 'text', req: true }, rowId: { t: 'text', req: true, index: true }, localRow: { t: 'json' }, remoteRow: { t: 'json' }, fields: { t: 'json', req: true },
  status: { t: 'enum', req: true, values: SYNC_CONFLICT_STATUSES, index: true }, reason: { t: 'text', req: true }, resolution: { t: 'text' },
  resolvedAt: { t: 'datetime' }, opId: { t: 'text' },
} as const satisfies Fields<SyncConflict>;

/** v5 — advisor / agency identity used on every client-facing document (one agency row, one row per advisor). */
export interface BrandingProfile extends Base {
  scope: BrandingScope; userId?: string | null; advisorName?: string | null; jobTitle?: string | null; agencyName?: string | null;
  licenceNumber?: string | null; licenceAuthority?: string | null; licenceExpiry?: string | null; country?: string | null;
  registration?: string | null; regulatoryText?: string | null; phone?: string | null; email?: string | null; website?: string | null;
  address?: string | null; mapsUrl?: string | null; logo?: string | null; logoName?: string | null; photo?: string | null;
  signature?: string | null; signOff?: string | null; coBranding?: CoBranding | null; showPhoto?: boolean | null; showSignature?: boolean | null;
  showRegulatory?: boolean | null; accent?: string | null;
}
export const BrandingProfileFields = {
  ...base,
  scope: { t: 'enum', req: true, values: BRANDING_SCOPES, index: true }, userId: { t: 'text', index: true },
  advisorName: { t: 'text' }, jobTitle: { t: 'text' }, agencyName: { t: 'text' }, licenceNumber: { t: 'text' }, licenceAuthority: { t: 'text' },
  licenceExpiry: { t: 'date' }, country: { t: 'text' }, registration: { t: 'text' }, regulatoryText: { t: 'text' }, phone: { t: 'text' },
  email: { t: 'text' }, website: { t: 'text' }, address: { t: 'text' }, mapsUrl: { t: 'text' }, logo: { t: 'text' }, logoName: { t: 'text' },
  photo: { t: 'text' }, signature: { t: 'text' }, signOff: { t: 'text' }, coBranding: { t: 'enum', values: CO_BRANDING },
  showPhoto: { t: 'bool' }, showSignature: { t: 'bool' }, showRegulatory: { t: 'bool' }, accent: { t: 'text' },
} as const satisfies Fields<BrandingProfile>;

/** v5 — document checklist for an insurer and/or product category (applied when an application starts). */
export interface ChecklistTemplate extends Base {
  name: string; insurer?: string | null; category?: string | null; branch?: string | null; items: unknown; active: boolean;
}
export const ChecklistTemplateFields = {
  ...base,
  name: { t: 'text', req: true }, insurer: { t: 'text', index: true }, category: { t: 'text' }, branch: { t: 'text' },
  items: { t: 'json', req: true }, active: { t: 'bool', req: true },
} as const satisfies Fields<ChecklistTemplate>;

// ---------------------------------------------------------------------------
// 0.6 — group plans (employer schemes: group life, medical, pension, accident) and their member census

export const GROUP_CATEGORIES = ['life', 'medical', 'pension', 'accident', 'mixed', 'other'] as const;
export const GROUP_STATUSES = ['quote', 'active', 'renewing', 'ended'] as const;
export const GROUP_PREMIUM_MODELS = ['per_member', 'total'] as const;
export const GROUP_MEMBER_STATUSES = ['active', 'pending', 'left'] as const;
export type GroupCategory = typeof GROUP_CATEGORIES[number];
export type GroupStatus = typeof GROUP_STATUSES[number];
export type GroupMemberStatus = typeof GROUP_MEMBER_STATUSES[number];
/** a cover class of a scheme (e.g. "Management", "Staff") with its own benefits and premium */
export interface GroupClass { id: string; name: string; benefits?: string | null; premiumPerMember?: number | null }
export interface GroupPlan extends Base, Ext {
  name: string; employerName: string; contactPersonId?: string | null; employerVat?: string | null;
  carrier?: string | null; policyNumber?: string | null; category: GroupCategory; status: GroupStatus;
  startDate?: string | null; renewalDate?: string | null; premiumModel: typeof GROUP_PREMIUM_MODELS[number];
  premiumPerMember?: number | null; totalAnnualPremium?: number | null; employerShare?: number | null;
  classes: GroupClass[]; notes?: string | null; ownerId?: string | null;
}
export const GroupPlanFields = {
  ...base, ...ext,
  name: { t: 'text', req: true }, employerName: { t: 'text', req: true, index: true }, contactPersonId: { t: 'ref', ref: 'persons', index: true }, employerVat: { t: 'text' },
  carrier: { t: 'text' }, policyNumber: { t: 'text' }, category: { t: 'enum', req: true, values: GROUP_CATEGORIES }, status: { t: 'enum', req: true, values: GROUP_STATUSES, index: true },
  startDate: { t: 'date' }, renewalDate: { t: 'date', index: true }, premiumModel: { t: 'enum', req: true, values: GROUP_PREMIUM_MODELS },
  premiumPerMember: { t: 'number' }, totalAnnualPremium: { t: 'number' }, employerShare: { t: 'number' },
  classes: { t: 'json', req: true }, notes: { t: 'text' }, ownerId: { t: 'text' },
} as const satisfies Fields<GroupPlan>;
export interface GroupMember extends Base, Ext {
  planId: string; personId?: string | null; displayName: string; firstName?: string | null; lastName?: string | null;
  birthDate?: string | null; email?: string | null; phone?: string | null; employeeNo?: string | null; classId?: string | null;
  joinDate?: string | null; leaveDate?: string | null; status: GroupMemberStatus; dependants?: number | null; notes?: string | null;
}
export const GroupMemberFields = {
  ...base, ...ext,
  planId: { t: 'ref', ref: 'group_plans', req: true, index: true }, personId: { t: 'ref', ref: 'persons', index: true },
  displayName: { t: 'text', req: true }, firstName: { t: 'text' }, lastName: { t: 'text' }, birthDate: { t: 'date' }, email: { t: 'text' }, phone: { t: 'text' },
  employeeNo: { t: 'text' }, classId: { t: 'text' }, joinDate: { t: 'date' }, leaveDate: { t: 'date' },
  status: { t: 'enum', req: true, values: GROUP_MEMBER_STATUSES, index: true }, dependants: { t: 'int' }, notes: { t: 'text' },
} as const satisfies Fields<GroupMember>;

// ---------------------------------------------------------------------------
// v8 — insured objects (general insurance): vehicles, properties, businesses linked to a person

export const INSURED_ITEM_KINDS = ['vehicle', 'property', 'business', 'other'] as const;
export const INSURED_ITEM_STATUSES = ['active', 'sold', 'archived'] as const;
export type InsuredItemKind = typeof INSURED_ITEM_KINDS[number];
export type InsuredItemStatus = typeof INSURED_ITEM_STATUSES[number];
export interface InsuredItem extends Base, Ext {
  personId: string; kind: InsuredItemKind; label: string; status: InsuredItemStatus;
  // vehicle
  regNo?: string | null; make?: string | null; model?: string | null; year?: number | null; value?: number | null;
  drivers?: string[] | null; ncdYears?: number | null; motDue?: string | null; roadTaxDue?: string | null;
  // property
  address?: string | null; areaSqm?: number | null; yearBuilt?: number | null; rebuildValue?: number | null; contentsValue?: number | null; mortgaged?: boolean | null;
  // business
  activity?: string | null; employees?: number | null;
  /** linked policies of the same person */
  policyIds?: string[] | null; notes?: string | null;
}
export const InsuredItemFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true },
  kind: { t: 'enum', req: true, values: INSURED_ITEM_KINDS }, label: { t: 'text', req: true }, status: { t: 'enum', req: true, values: INSURED_ITEM_STATUSES, index: true },
  regNo: { t: 'text', index: true }, make: { t: 'text' }, model: { t: 'text' }, year: { t: 'int' }, value: { t: 'number' },
  drivers: { t: 'json' }, ncdYears: { t: 'int' }, motDue: { t: 'date' }, roadTaxDue: { t: 'date' },
  address: { t: 'text' }, areaSqm: { t: 'number' }, yearBuilt: { t: 'int' }, rebuildValue: { t: 'number' }, contentsValue: { t: 'number' }, mortgaged: { t: 'bool' },
  activity: { t: 'text' }, employees: { t: 'int' },
  policyIds: { t: 'json' }, notes: { t: 'text' },
} as const satisfies Fields<InsuredItem>;

// ---------------------------------------------------------------------------
// v9 — premium collections that are overdue or failed (entered by the advisor or imported from an insurer's list)

export const PREMIUM_ARREAR_STATUSES = ['open', 'paid', 'lapsed'] as const;
export const PREMIUM_ARREAR_SOURCES = ['manual', 'import'] as const;
export type PremiumArrearStatus = typeof PREMIUM_ARREAR_STATUSES[number];
export type PremiumArrearSource = typeof PREMIUM_ARREAR_SOURCES[number];
export interface PremiumArrear extends Base, Ext {
  policyId: string; personId: string; amount: number; currency?: string | null; dueDate: string;
  status: PremiumArrearStatus; source: PremiumArrearSource; note?: string | null;
  /** when it was marked paid or lapsed */
  resolvedAt?: string | null;
  /** the policy number as written in the insurer's list (imports) and the list it came from */
  policyNumber?: string | null; importRef?: string | null;
}
export const PremiumArrearFields = {
  ...base, ...ext,
  policyId: { t: 'ref', ref: 'policies', req: true, index: true }, personId: { t: 'ref', ref: 'persons', req: true, index: true },
  amount: { t: 'number', req: true }, currency: { t: 'text' }, dueDate: { t: 'date', req: true, index: true },
  status: { t: 'enum', req: true, values: PREMIUM_ARREAR_STATUSES, index: true }, source: { t: 'enum', req: true, values: PREMIUM_ARREAR_SOURCES },
  note: { t: 'text' }, resolvedAt: { t: 'datetime' }, policyNumber: { t: 'text' }, importRef: { t: 'text' },
} as const satisfies Fields<PremiumArrear>;

// ---------------------------------------------------------------------------
// v10 — ideas track (Oct 2026): client feedback (NPS), KYC/AML file, claims and changes reported from the client portal

export const NPS_TRIGGERS = ['meeting', 'claim', 'manual'] as const;
export const NPS_STATUSES = ['prepared', 'answered', 'withdrawn'] as const;
export const NPS_CATEGORIES = ['promoter', 'passive', 'detractor'] as const;
export type NpsTrigger = typeof NPS_TRIGGERS[number];
export type NpsStatus = typeof NPS_STATUSES[number];
export type NpsCategory = typeof NPS_CATEGORIES[number];
/** one survey link prepared for a client (the advisor sends the message himself); the answer arrives from the platform */
export interface NpsSurvey extends Base, Ext {
  personId: string; trigger: NpsTrigger; refTable?: string | null; refId?: string | null;
  /** the random part of the survey link (served by the platform) */
  token: string; lang?: string | null; advisorName?: string | null;
  status: NpsStatus; preparedAt: string; expiresAt?: string | null;
  score?: number | null; comment?: string | null; answeredAt?: string | null; category?: NpsCategory | null;
  /** 0–6: the call-back task; 9–10: when the advisor opened the Google-review draft */
  taskId?: string | null; reviewAskedAt?: string | null;
}
export const NpsSurveyFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true }, trigger: { t: 'enum', req: true, values: NPS_TRIGGERS },
  refTable: { t: 'text' }, refId: { t: 'text', index: true }, token: { t: 'text', req: true, index: true }, lang: { t: 'text' }, advisorName: { t: 'text' },
  status: { t: 'enum', req: true, values: NPS_STATUSES, index: true }, preparedAt: { t: 'datetime', req: true }, expiresAt: { t: 'datetime' },
  score: { t: 'int' }, comment: { t: 'text' }, answeredAt: { t: 'datetime', index: true }, category: { t: 'enum', values: NPS_CATEGORIES },
  taskId: { t: 'ref', ref: 'tasks' }, reviewAskedAt: { t: 'datetime' },
} as const satisfies Fields<NpsSurvey>;

export const KYC_PEP = ['no', 'yes', 'unknown'] as const;
export const KYC_RISK = ['low', 'standard', 'high'] as const;
export const KYC_FUNDS = ['salary', 'business', 'savings', 'inheritance', 'property_sale', 'investments', 'pension', 'gift', 'other'] as const;
export type KycPep = typeof KYC_PEP[number];
export type KycRisk = typeof KYC_RISK[number];
/** a named beneficiary (name, relationship, share %) — never an identity-card or passport number */
export interface KycBeneficiary { name: string; relationship?: string | null; share?: number | null }
/** know-your-client / anti-money-laundering file for a client's life business (one per person) */
export interface KycFile extends Base, Ext {
  personId: string;
  /** the identity document is kept in the vault: a link to it, never its number */
  idDocumentId?: string | null; idExpiry?: string | null;
  beneficiaries?: KycBeneficiary[] | null;
  pep: KycPep; pepNote?: string | null;
  sourceOfFunds?: string[] | null; fundsNote?: string | null;
  risk?: KycRisk | null;
  checkedAt?: string | null; nextReviewDate?: string | null;
  notes?: string | null;
}
export const KycFileFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true },
  idDocumentId: { t: 'ref', ref: 'documents' }, idExpiry: { t: 'date' },
  beneficiaries: { t: 'json' }, pep: { t: 'enum', req: true, values: KYC_PEP }, pepNote: { t: 'text' },
  sourceOfFunds: { t: 'json' }, fundsNote: { t: 'text' }, risk: { t: 'enum', values: KYC_RISK },
  checkedAt: { t: 'date' }, nextReviewDate: { t: 'date', index: true }, notes: { t: 'text' },
} as const satisfies Fields<KycFile>;

// ---------------------------------------------------------------------------
// v11 — sale file per sale (IDD, Directive (EU) 2016/97, and Cyprus Law 38(I)/2016 as amended): demands and needs,
// recommendation, the pre-contractual disclosures and the documents given — one record per sale, never deleted

export const SALE_LINES = ['life', 'general', 'ibip'] as const;
export const SALE_ADVICE = ['advised', 'non_advised'] as const;
export const SALE_CHANNELS = ['face_to_face', 'distance'] as const;
export const SALE_FILE_STATUSES = ['open', 'complete'] as const;
export type SaleLine = typeof SALE_LINES[number];
export type SaleAdvice = typeof SALE_ADVICE[number];
export type SaleChannel = typeof SALE_CHANNELS[number];
export type SaleFileStatus = typeof SALE_FILE_STATUSES[number];
/** one item of the sale checklist: when it was given / done, optionally the document in the vault */
export interface SaleCheck { at?: string | null; documentId?: string | null; note?: string | null }
export interface SaleFile extends Base, Ext {
  personId: string; policyId?: string | null; caseId?: string | null; needsAnalysisId?: string | null;
  line: SaleLine; advice: SaleAdvice; channel: SaleChannel;
  product?: string | null; insurer?: string | null; saleDate: string;
  /** the client's demands and needs, in the advisor's words (IDD art. 20(1)) */
  demands?: string | null;
  /** advised sales: why the proposed product meets those demands and needs */
  recommendation?: string | null;
  checks?: Record<string, SaleCheck> | null;
  status: SaleFileStatus; completedAt?: string | null; notes?: string | null;
  /** v12: the client's risk profile the suitability assessment relied on */
  riskProfileId?: string | null;
}
export const SaleFileFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true }, policyId: { t: 'ref', ref: 'policies', index: true }, caseId: { t: 'ref', ref: 'cases' },
  needsAnalysisId: { t: 'ref', ref: 'needs_analyses' },
  line: { t: 'enum', req: true, values: SALE_LINES }, advice: { t: 'enum', req: true, values: SALE_ADVICE }, channel: { t: 'enum', req: true, values: SALE_CHANNELS },
  product: { t: 'text' }, insurer: { t: 'text' }, saleDate: { t: 'date', req: true, index: true },
  demands: { t: 'text' }, recommendation: { t: 'text' }, checks: { t: 'json' },
  status: { t: 'enum', req: true, values: SALE_FILE_STATUSES, index: true }, completedAt: { t: 'datetime' }, notes: { t: 'text' },
  riskProfileId: { t: 'ref', ref: 'risk_profiles' },
} as const satisfies Fields<SaleFile>;

// ---------------------------------------------------------------------------
// v12 — prospecting by phone («Τηλεφωνική προσέγγιση»: one row per dial, for the advisor's own statistics), risk profile
// & suitability (dated, versioned; imported from avrastro.com or filled in by the advisor), the family map in the client
// card, and client-facing summaries (after a meeting, the yearly transparency summary). All additive.

export const CALL_OUTCOMES = ['no_answer', 'busy', 'wrong_number', 'not_interested', 'callback', 'interested', 'meeting', 'do_not_call'] as const;
export type CallOutcome = typeof CALL_OUTCOMES[number];
/** one dial: who, when (local day and hour, for the statistics), how long, what came of it */
export interface CallLog extends Base, Ext {
  listId?: string | null; prospectId?: string | null; personId?: string | null; phone?: string | null;
  at: string; day: string; hour: number; durationSec?: number | null; outcome: CallOutcome;
  /** the opening-line variant used (A / B) */
  variant?: string | null;
  /** call back later: when; meeting: when the meeting is */
  callbackAt?: string | null;
  /** the call-back / follow-up task or the appointment in the agenda */
  taskId?: string | null; notes?: string | null;
}
export const CallLogFields = {
  ...base, ...ext,
  listId: { t: 'ref', ref: 'prospect_lists', index: true }, prospectId: { t: 'ref', ref: 'prospects', index: true }, personId: { t: 'ref', ref: 'persons', index: true },
  phone: { t: 'text' }, at: { t: 'datetime', req: true }, day: { t: 'date', req: true, index: true }, hour: { t: 'int', req: true },
  durationSec: { t: 'int' }, outcome: { t: 'enum', req: true, values: CALL_OUTCOMES, index: true }, variant: { t: 'text' },
  callbackAt: { t: 'datetime' }, taskId: { t: 'ref', ref: 'tasks' }, notes: { t: 'text' },
} as const satisfies Fields<CallLog>;

export const RISK_LEVELS = ['conservative', 'cautious', 'balanced', 'growth', 'dynamic'] as const;
export const RISK_PROFILE_SOURCES = ['website', 'manual'] as const;
export type RiskLevel = typeof RISK_LEVELS[number];
export type RiskProfileSource = typeof RISK_PROFILE_SOURCES[number];
/** one answer of a risk questionnaire, as it was given (question text kept, so a later questionnaire change never rewrites it) */
export interface RiskAnswer { key: string; question?: string | null; answer?: string | null; points?: number | null }
/** a dated risk profile; a new one is a new revision (the earlier ones stay as the record) */
export interface RiskProfile extends Base, Ext {
  personId: string; date: string; revision: number; source: RiskProfileSource; level: RiskLevel;
  score?: number | null; maxScore?: number | null; answers?: RiskAnswer[] | null;
  /** the goal the money is for, and when it is needed (time to goal) */
  goal?: string | null; goalDate?: string | null; horizonYears?: number | null;
  /** the questionnaire version, or the website tool's version */
  sourceVersion?: string | null; sourceRef?: string | null; notes?: string | null;
}
export const RiskProfileFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true }, date: { t: 'date', req: true, index: true }, revision: { t: 'int', req: true },
  source: { t: 'enum', req: true, values: RISK_PROFILE_SOURCES }, level: { t: 'enum', req: true, values: RISK_LEVELS },
  score: { t: 'number' }, maxScore: { t: 'number' }, answers: { t: 'json' },
  goal: { t: 'text' }, goalDate: { t: 'date' }, horizonYears: { t: 'number' },
  sourceVersion: { t: 'text' }, sourceRef: { t: 'text' }, notes: { t: 'text' },
} as const satisfies Fields<RiskProfile>;

export const FAMILY_RELATIONS = ['spouse', 'partner', 'child', 'parent', 'grandparent', 'grandchild', 'sibling', 'godparent', 'godchild', 'other'] as const;
export const FAMILY_PAYS = ['none', 'pays_for', 'paid_by'] as const;
export type FamilyRelation = typeof FAMILY_RELATIONS[number];
export type FamilyPays = typeof FAMILY_PAYS[number];
/** a relative of a client: another person in the book, or just a name (a godparent, a grandparent) */
export interface FamilyLink extends Base, Ext {
  personId: string; relation: FamilyRelation; relatedPersonId?: string | null; name?: string | null;
  birthDate?: string | null; baptismDate?: string | null;
  /** who pays for whom: the client pays for this relative's cover (pays_for) or the relative pays for the client's (paid_by) */
  pays?: FamilyPays | null; notes?: string | null;
}
export const FamilyLinkFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true }, relation: { t: 'enum', req: true, values: FAMILY_RELATIONS },
  relatedPersonId: { t: 'ref', ref: 'persons', index: true }, name: { t: 'text' }, birthDate: { t: 'date' }, baptismDate: { t: 'date' },
  pays: { t: 'enum', values: FAMILY_PAYS }, notes: { t: 'text' },
} as const satisfies Fields<FamilyLink>;

export const SUMMARY_KINDS = ['meeting', 'transparency'] as const;
export const SUMMARY_STATUSES = ['draft', 'final', 'shared'] as const;
export type SummaryKind = typeof SUMMARY_KINDS[number];
export type SummaryStatus = typeof SUMMARY_STATUSES[number];
/** a client-facing summary in the client's language: after a meeting, or the yearly transparency summary */
export interface ClientSummary extends Base, Ext {
  personId: string; kind: SummaryKind; meetingId?: string | null; lang: string; date: string; title: string;
  /** what the client reads (sections, items, next steps) — never internal notes */
  content: unknown;
  status: SummaryStatus; periodFrom?: string | null; periodTo?: string | null; sharedAt?: string | null; printedAt?: string | null;
}
export const ClientSummaryFields = {
  ...base, ...ext,
  personId: { t: 'ref', ref: 'persons', req: true, index: true }, kind: { t: 'enum', req: true, values: SUMMARY_KINDS, index: true },
  meetingId: { t: 'ref', ref: 'meetings' }, lang: { t: 'text', req: true }, date: { t: 'date', req: true, index: true }, title: { t: 'text', req: true },
  content: { t: 'json', req: true }, status: { t: 'enum', req: true, values: SUMMARY_STATUSES },
  periodFrom: { t: 'date' }, periodTo: { t: 'date' }, sharedAt: { t: 'datetime' }, printedAt: { t: 'datetime' },
} as const satisfies Fields<ClientSummary>;

// v15 (handoff v08 #25 «Απολογισμός ημέρας»): one end-of-day report per day (id = stableId('daily-report', day)).
// `recorded` is the snapshot of what the CRM recorded when the report was saved (calls, tasks, meetings, campaign
// outcomes — packages/domain/src/performance.ts); `manual` holds the advisor's own totals per metric («δική μου
// καταχώριση»: a total for the day, never added on top of the recorded number — the larger of the two counts);
// `goals` the day's objectives (from the daily goals when present, otherwise entered by the advisor).
export interface DailyReport extends Base, Ext {
  day: string; recorded: unknown; manual?: unknown; goals?: unknown; selfRating?: number | null; notes?: string | null;
  /** the feedback lines the advisor kept / edited: { hidden: string[], text: string | null } */
  feedback?: unknown; savedAt: string; advisorId?: string | null;
}
export const DailyReportFields = {
  ...base, ...ext,
  day: { t: 'date', req: true, index: true }, recorded: { t: 'json', req: true }, manual: { t: 'json' }, goals: { t: 'json' },
  selfRating: { t: 'int' }, notes: { t: 'text' }, feedback: { t: 'json' }, savedAt: { t: 'datetime', req: true }, advisorId: { t: 'text' },
} as const satisfies Fields<DailyReport>;

// ---------------------------------------------------------------------------
// v14 — the advisor's insurer & product catalogue (handoff v08 #14). Per workspace (each advisor's own; a team
// workspace shares it under the workspace's role rules). Never hard-deleted while a policy or a child uses it:
// deactivated instead (hidden from new policies, kept on the policies that have it).

/** an insurance company the advisor works with */
export interface CatalogInsurer extends Base, Ext {
  name: string; code?: string | null; sortOrder: number; active: boolean; deactivatedAt?: string | null; notes?: string | null;
}
export const CatalogInsurerFields = {
  ...base, ...ext,
  name: { t: 'text', req: true }, code: { t: 'text' }, sortOrder: { t: 'int', req: true }, active: { t: 'bool', req: true },
  deactivatedAt: { t: 'datetime' }, notes: { t: 'text' },
} as const satisfies Fields<CatalogInsurer>;

/** an insurer's product category, as the advisor names it (e.g. «Life Protection»), mapped to the policy category */
export interface CatalogCategory extends Base, Ext {
  insurerId: string; name: string; branch: PolicyBranch; policyCategory?: PolicyCategory | null;
  sortOrder: number; active: boolean; deactivatedAt?: string | null; notes?: string | null;
}
export const CatalogCategoryFields = {
  ...base, ...ext,
  insurerId: { t: 'ref', req: true, ref: 'catalog_insurers', index: true }, name: { t: 'text', req: true },
  branch: { t: 'enum', req: true, values: POLICY_BRANCHES }, policyCategory: { t: 'enum', values: POLICY_CATEGORIES },
  sortOrder: { t: 'int', req: true }, active: { t: 'bool', req: true }, deactivatedAt: { t: 'datetime' }, notes: { t: 'text' },
} as const satisfies Fields<CatalogCategory>;

/** a specific named plan of an insurer's category (e.g. «Life Plan 1») */
export interface CatalogPlan extends Base, Ext {
  insurerId: string; categoryId: string; name: string; code?: string | null; branch?: PolicyBranch | null;
  sortOrder: number; active: boolean; deactivatedAt?: string | null;
  /** the period the insurer sells the plan (information; selection is driven by `active`) */
  activeFrom?: string | null; activeTo?: string | null; notes?: string | null;
}
export const CatalogPlanFields = {
  ...base, ...ext,
  insurerId: { t: 'ref', req: true, ref: 'catalog_insurers', index: true }, categoryId: { t: 'ref', req: true, ref: 'catalog_categories', index: true },
  name: { t: 'text', req: true }, code: { t: 'text' }, branch: { t: 'enum', values: POLICY_BRANCHES },
  sortOrder: { t: 'int', req: true }, active: { t: 'bool', req: true }, deactivatedAt: { t: 'datetime' },
  activeFrom: { t: 'date' }, activeTo: { t: 'date' }, notes: { t: 'text' },
} as const satisfies Fields<CatalogPlan>;
// schema v16 — AI business coach, part 1 (handoff v08 #22, #23): the adaptive daily goals of a working day (one snapshot
// per day: what was proposed, why, what the advisor adjusted or skipped) and the advisor's own blocks in the hour-by-hour
// planner (focus / personal time, planned tasks, accepted goal suggestions). Appointments stay tasks; nothing here moves them.

export const DAILY_GOAL_TYPES = ['overdue_followups', 'renewals', 'customer_contacts', 'prospecting_calls', 'appointments'] as const;
export type DailyGoalType = typeof DAILY_GOAL_TYPES[number];
export const DAILY_GOAL_STATUSES = ['active', 'skipped'] as const;
export type DailyGoalStatus = typeof DAILY_GOAL_STATUSES[number];
/** one reason a goal was proposed (an i18n key under goals.why.* and its numbers) */
export interface GoalReason { key: string; params: Record<string, string | number> }
export interface DailyGoal {
  type: DailyGoalType;
  /** the goal for the day (the suggestion, or the advisor's own number) */
  target: number;
  /** what the computation proposed */
  suggested: number;
  status: DailyGoalStatus;
  /** the advisor changed the number (a recomputation keeps it) */
  adjusted: boolean;
  reasons: GoalReason[];
  /** ids the goal is about (the overdue tasks, the renewal clients) — progress counts only these */
  refs: string[];
  /** planning minutes per unit (the planner's suggestion blocks) */
  minutesEach: number;
}
/** a market note for the day — only one the advisor added, with its source; never generated */
export interface DailyMarketNote { text: string; source: string; url?: string | null; addedAt: string }
export interface DailyGoalSnapshot extends Base, Ext {
  day: string; computedAt: string;
  goals: DailyGoal[];
  /** the inputs the goals were computed from (free time, counts, pace, recent completion) */
  inputs: unknown;
  /** planner suggestions the advisor dismissed for this day (keys) */
  dismissed?: string[] | null;
  market?: DailyMarketNote | null;
}
export const DailyGoalSnapshotFields = {
  ...base, ...ext,
  day: { t: 'date', req: true, index: true }, computedAt: { t: 'datetime', req: true },
  goals: { t: 'json', req: true }, inputs: { t: 'json', req: true }, dismissed: { t: 'json' }, market: { t: 'json' },
} as const satisfies Fields<DailyGoalSnapshot>;

export const PLAN_BLOCK_KINDS = ['focus', 'personal', 'task', 'goal', 'admin', 'travel', 'break'] as const;
export type PlanBlockKind = typeof PLAN_BLOCK_KINDS[number];
export const PLAN_BLOCK_STATUSES = ['planned', 'done', 'cancelled'] as const;
export type PlanBlockStatus = typeof PLAN_BLOCK_STATUSES[number];
export const PLAN_BLOCK_SOURCES = ['advisor', 'suggestion'] as const;
export type PlanBlockSource = typeof PLAN_BLOCK_SOURCES[number];
/** v17: what the advisor does in a block (owner 22 Oct 2026). The insurance appointment itself is NOT a block type: it is
 *  a CRM appointment (tasks.kind «appointment»), so it keeps counting in the KPIs. «coffee» and «referral_meeting» are
 *  relationship meetings — counted on their own, never as appointments. `kind` stays one of the schema-16 values (an app
 *  of schema 16 reads a sensible kind; the server keeps `activity` when that app writes the block back). */
export const PLAN_ACTIVITIES = ['coffee', 'referral_meeting', 'calls', 'followup', 'admin', 'prep', 'learning', 'team', 'travel', 'break', 'personal', 'other'] as const;
export type PlanActivity = typeof PLAN_ACTIVITIES[number];
/** a block of the advisor's own day plan (local day, HH:MM – HH:MM); cancelled blocks are kept */
export interface PlanBlock extends Base, Ext {
  day: string; start: string; end: string; kind: PlanBlockKind; title: string; status: PlanBlockStatus; source: PlanBlockSource;
  /** a task planned into this time (the task keeps its own date; completing the block completes it) */
  taskId?: string | null;
  /** the daily goal an accepted suggestion works on */
  goalType?: string | null;
  notes?: string | null; completedAt?: string | null;
  /** v17: the type of the block (see PLAN_ACTIVITIES); null on blocks made before 17 (their `kind` says it) */
  activity?: PlanActivity | null;
  /** v17: the person a relationship meeting (coffee, referral) is with — optional; on completion it is on their timeline */
  personId?: string | null;
}
export const PlanBlockFields = {
  ...base, ...ext,
  day: { t: 'date', req: true, index: true }, start: { t: 'time', req: true }, end: { t: 'time', req: true },
  kind: { t: 'enum', req: true, values: PLAN_BLOCK_KINDS }, title: { t: 'text', req: true },
  status: { t: 'enum', req: true, values: PLAN_BLOCK_STATUSES, index: true }, source: { t: 'enum', req: true, values: PLAN_BLOCK_SOURCES },
  taskId: { t: 'ref', ref: 'tasks', index: true }, goalType: { t: 'text' }, notes: { t: 'text' }, completedAt: { t: 'datetime' },
  activity: { t: 'enum', values: PLAN_ACTIVITIES }, personId: { t: 'ref', ref: 'persons', index: true },
} as const satisfies Fields<PlanBlock>;

// ---------------------------------------------------------------------------
// v18 — «Ο φορολογικός μου φάκελος»: the advisor's OWN tax folder (their business as a self-employed advisor, not clients).
// Private to the member who keeps it: the sync server serves these rows (and documents with `taxYearId`) only to the
// member who created them and to the members whose e-mail is in the year's `sharedWith` (read-only) — whatever the role
// (packages/sync/src/protocol.ts `privateTable`). Organisation of documents only: nothing here computes tax.
export const TAX_ITEM_STATUSES = ['missing', 'received', 'checked'] as const;
export type TaxItemStatus = typeof TAX_ITEM_STATUSES[number];
/** the advisor's own marking for the accountant — never a statement by AVRASTRO that something is deductible */
export const TAX_DECLARE = ['yes', 'no', 'ask'] as const;
export type TaxDeclare = typeof TAX_DECLARE[number];
export const TAX_YEAR_STATUSES = ['open', 'sent', 'closed'] as const;
export type TaxYearStatus = typeof TAX_YEAR_STATUSES[number];
/** a category the advisor renamed, hid or added (key `custom_…`) for one year */
export interface TaxCategoryEdit { key: string; label?: string | null; hidden?: boolean | null; custom?: boolean | null; group?: string | null }
export interface TaxYear extends Base, Ext {
  year: number;
  /** the sync member id of the advisor who keeps the folder (null on a device that was never paired); informative —
   *  the server decides ownership from who first stored the row */
  ownerUserId?: string | null;
  /** e-mails of team members who may READ this folder (explicit share by the owner) */
  sharedWith?: string[] | null;
  status: TaxYearStatus;
  /** general notes for the accountant */
  notes?: string | null;
  accountantName?: string | null; accountantEmail?: string | null;
  sentAt?: string | null;
  categories?: TaxCategoryEdit[] | null;
  /** the reminder task «Ετοιμάστε τον φορολογικό φάκελο {year}» (tasks.id) */
  reminderTaskId?: string | null;
}
export const TaxYearFields = {
  ...base, ...ext,
  year: { t: 'int', req: true, index: true }, ownerUserId: { t: 'text', index: true }, sharedWith: { t: 'json' },
  status: { t: 'enum', req: true, values: TAX_YEAR_STATUSES }, notes: { t: 'text' }, accountantName: { t: 'text' }, accountantEmail: { t: 'text' },
  sentAt: { t: 'datetime' }, categories: { t: 'json' }, reminderTaskId: { t: 'ref', ref: 'tasks' },
} as const satisfies Fields<TaxYear>;
/** one document or receipt of the folder */
export interface TaxItem extends Base, Ext {
  yearId: string; date?: string | null; amount?: number | null; currency?: string | null; issuer?: string | null;
  category: string; subcategory?: string | null; declare: TaxDeclare; notes?: string | null;
  /** the stored files (documents.id, each with taxYearId) */
  documentIds?: string[] | null;
  status: TaxItemStatus;
  /** the expected document (checklist slot) this item answers */
  expectedId?: string | null;
  /** 'YYYY-MM' for a monthly slot, 'YYYY-Qn' for a quarter */
  period?: string | null;
  /** an own policy (annual certificate of premiums) */
  policyId?: string | null;
  source?: string | null; checkedAt?: string | null;
}
export const TaxItemFields = {
  ...base, ...ext,
  yearId: { t: 'ref', req: true, ref: 'tax_years', index: true }, date: { t: 'date' }, amount: { t: 'number' }, currency: { t: 'text' }, issuer: { t: 'text' },
  category: { t: 'text', req: true, index: true }, subcategory: { t: 'text' }, declare: { t: 'enum', req: true, values: TAX_DECLARE }, notes: { t: 'text' },
  documentIds: { t: 'json' }, status: { t: 'enum', req: true, values: TAX_ITEM_STATUSES, index: true }, expectedId: { t: 'ref', ref: 'tax_expected', index: true },
  period: { t: 'text' }, policyId: { t: 'ref', ref: 'policies' }, source: { t: 'text' }, checkedAt: { t: 'datetime' },
} as const satisfies Fields<TaxItem>;
/** an expected document of the year's checklist (e.g. «Κοινωνικές ασφαλίσεις — Μάρτιος», «Βεβαίωση ασφαλίστρων — πολ. 123») */
export interface TaxExpected extends Base, Ext {
  yearId: string; category: string; title: string; period?: string | null; dueDate?: string | null; policyId?: string | null;
  /** the quick-add series it belongs to (e.g. 'social_insurance:monthly') — adding the series twice adds nothing */
  seriesKey?: string | null;
  /** «not needed this year» — kept, not deleted */
  waived?: boolean | null;
  notes?: string | null;
}
export const TaxExpectedFields = {
  ...base, ...ext,
  yearId: { t: 'ref', req: true, ref: 'tax_years', index: true }, category: { t: 'text', req: true }, title: { t: 'text', req: true },
  period: { t: 'text' }, dueDate: { t: 'date' }, policyId: { t: 'ref', ref: 'policies' }, seriesKey: { t: 'text', index: true }, waived: { t: 'bool' }, notes: { t: 'text' },
} as const satisfies Fields<TaxExpected>;

// ---------------------------------------------------------------------------
// Table registry

export const TABLES = {
  persons: PersonFields,
  households: HouseholdFields,
  household_members: HouseholdMemberFields,
  policies: PolicyFields,
  policy_adjustments: PolicyAdjustmentFields,
  cases: CaseFields,
  case_requirements: CaseRequirementFields,
  case_events: CaseEventFields,
  tasks: TaskFields,
  notes: NoteFields,
  reviews: ReviewFields,
  timeline: TimelineEventFields,
  prospect_lists: ProspectListFields,
  prospects: ProspectFields,
  documents: DocumentFields,
  service_cases: ServiceCaseFields,
  consents: ConsentFields,
  legacy_records: LegacyRecordFields,
  comparisons: ComparisonFields,
  needs_analyses: NeedsAnalysisFields,
  quotations: QuotationFields,
  opportunities: OpportunityFields,
  referrals: ReferralFields,
  communications: CommunicationFields,
  meetings: MeetingFields,
  claims: ClaimFields,
  templates: TemplateFields,
  settings: SettingFields,
  audit_events: AuditEventFields,
  production_events: ProductionEventFields,
  renewals: RenewalFields,
  commission_rules: CommissionRuleFields,
  commission_entries: CommissionEntryFields,
  tax_rulesets: TaxRulesetFields,
  tax_snapshots: TaxSnapshotFields,
  financial_items: FinancialItemFields,
  life_events: LifeEventFields,
  campaigns: CampaignFields,
  campaign_activities: CampaignActivityFields,
  automation_rules: AutomationRuleFields,
  automation_runs: AutomationRunFields,
  extraction_drafts: ExtractionDraftFields,
  custom_fields: CustomFieldFields,
  sync_ops: SyncOpFields,
  sync_conflicts: SyncConflictFields,
  branding_profiles: BrandingProfileFields,
  checklist_templates: ChecklistTemplateFields,
  group_plans: GroupPlanFields,
  group_members: GroupMemberFields,
  insured_items: InsuredItemFields,
  premium_arrears: PremiumArrearFields,
  nps_surveys: NpsSurveyFields,
  kyc_files: KycFileFields,
  sale_files: SaleFileFields,
  call_logs: CallLogFields,
  risk_profiles: RiskProfileFields,
  family_links: FamilyLinkFields,
  client_summaries: ClientSummaryFields,
  catalog_insurers: CatalogInsurerFields,
  catalog_categories: CatalogCategoryFields,
  catalog_plans: CatalogPlanFields,
  daily_reports: DailyReportFields,
  daily_goals: DailyGoalSnapshotFields,
  plan_blocks: PlanBlockFields,
  tax_years: TaxYearFields,
  tax_items: TaxItemFields,
  tax_expected: TaxExpectedFields,
} as const;

export type TableName = keyof typeof TABLES;
export const TABLE_NAMES = Object.keys(TABLES) as TableName[];
/** Device-local tables: never exported in backups, never replicated, never audited. */
export const LOCAL_TABLES: readonly TableName[] = ['sync_ops', 'sync_conflicts'];
export const isLocalTable = (t: string) => (LOCAL_TABLES as readonly string[]).includes(t);

/**
 * v14: what each schema version added that an app of an earlier version does not know. The sync server uses it to
 * serve an older app safely: it leaves out rows of tables that app does not have and the fields it would reject, and
 * keeps those fields when that app writes the row back (so its edit never erases them). The client's schema comes from
 * requirement 21's sync handshake (x-vero-schema; no header = schema 11, the 0.7.1 desktop apps). Additions from 12 on are listed.
 */
export const SCHEMA_ADDITIONS: Record<number, { tables?: readonly TableName[]; fields?: Partial<Record<TableName, readonly string[]>> }> = {
  // 12 and 13 (Oct 2026): the 0.7.1 desktop apps in the field are schema 11 and send no handshake — without these
  // rows they received call_logs / risk_profiles / … and stopped syncing («this.data[e].map»)
  12: {
    tables: ['call_logs', 'risk_profiles', 'family_links', 'client_summaries'],
    fields: { persons: ['doNotCall'], prospect_lists: ['scripts', 'lawfulBasis', 'consentSource'], prospects: ['doNotCall', 'lawfulBasis', 'consentSource', 'consentDate', 'scriptVariant', 'attempts'], sale_files: ['riskProfileId'] },
  },
  13: { fields: { policies: ['deletedAt', 'deletedBy', 'deleteReason'] } },
  14: { tables: ['catalog_insurers', 'catalog_categories', 'catalog_plans'], fields: { policies: ['catalogInsurerId', 'catalogCategoryId', 'catalogPlanId'] } },
  15: { tables: ['daily_reports'] },
  16: { tables: ['daily_goals', 'plan_blocks'] },
  17: { fields: { plan_blocks: ['activity', 'personId'] } },
  // «Ο φορολογικός μου φάκελος» (29 Oct 2026). Older apps get neither the tables nor the private documents at all
  // (sync server: an app below 18 cannot keep them private, see SyncServer.pull)
  18: { tables: ['tax_years', 'tax_items', 'tax_expected'], fields: { documents: ['taxYearId'] } },
};
/** tables an app of schema `version` does not know */
export function tablesAfter(version: number): Set<string> {
  const out = new Set<string>();
  for (const [v, a] of Object.entries(SCHEMA_ADDITIONS)) if (Number(v) > version) for (const t of a.tables ?? []) out.add(t);
  return out;
}
/** fields of `table` an app of schema `version` does not know */
export function fieldsAfter(table: string, version: number): string[] {
  const out: string[] = [];
  for (const [v, a] of Object.entries(SCHEMA_ADDITIONS)) if (Number(v) > version) out.push(...((a.fields as Record<string, readonly string[]> | undefined)?.[table] ?? []));
  return out;
}

export interface RowTypes {
  persons: Person; households: Household; household_members: HouseholdMember; policies: Policy;
  policy_adjustments: PolicyAdjustment; cases: Case; case_requirements: CaseRequirement; case_events: CaseEvent;
  tasks: Task; notes: Note; reviews: Review; timeline: TimelineEvent; prospect_lists: ProspectList; prospects: Prospect;
  documents: DocumentRecord; service_cases: ServiceCase; consents: Consent; legacy_records: LegacyRecord; comparisons: Comparison;
  needs_analyses: NeedsAnalysis; quotations: Quotation; opportunities: Opportunity; referrals: Referral; communications: Communication; meetings: Meeting; claims: Claim; templates: Template;
  settings: Setting; audit_events: AuditEvent;
  production_events: ProductionEvent; renewals: Renewal; commission_rules: CommissionRule; commission_entries: CommissionEntry;
  tax_rulesets: TaxRulesetRow; tax_snapshots: TaxSnapshot; financial_items: FinancialItem; life_events: LifeEvent; campaigns: Campaign;
  campaign_activities: CampaignActivity; automation_rules: AutomationRule; automation_runs: AutomationRun; extraction_drafts: ExtractionDraft;
  custom_fields: CustomField; sync_ops: SyncOp; sync_conflicts: SyncConflict;
  branding_profiles: BrandingProfile; checklist_templates: ChecklistTemplate;
  group_plans: GroupPlan; group_members: GroupMember;
  insured_items: InsuredItem;
  premium_arrears: PremiumArrear;
  nps_surveys: NpsSurvey;
  kyc_files: KycFile;
  sale_files: SaleFile;
  call_logs: CallLog; risk_profiles: RiskProfile; family_links: FamilyLink; client_summaries: ClientSummary;
  catalog_insurers: CatalogInsurer; catalog_categories: CatalogCategory; catalog_plans: CatalogPlan;
  daily_reports: DailyReport;
  daily_goals: DailyGoalSnapshot; plan_blocks: PlanBlock;
  tax_years: TaxYear; tax_items: TaxItem; tax_expected: TaxExpected;
}
export type AnyRow = RowTypes[TableName];
export type Dataset = { [K in TableName]: RowTypes[K][] };

export function emptyDataset(): Dataset {
  const d = {} as Record<TableName, unknown[]>;
  for (const t of TABLE_NAMES) d[t] = [];
  return d as Dataset;
}

// ---------------------------------------------------------------------------
// Validation

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;
const DT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface ValidationIssue { table: TableName; id: string; field: string; problem: string }

export function validateRow(table: TableName, row: Record<string, unknown>): ValidationIssue[] {
  const fields = TABLES[table] as Record<string, FieldDef>;
  const issues: ValidationIssue[] = [];
  const id = String(row.id ?? '?');
  for (const [name, def] of Object.entries(fields)) {
    const v = row[name];
    if (v === undefined || v === null || v === '') {
      if (def.req) issues.push({ table, id, field: name, problem: 'required' });
      continue;
    }
    let ok = true;
    switch (def.t) {
      case 'id': case 'ref': ok = typeof v === 'string' && UUID.test(v); break;
      case 'text': ok = typeof v === 'string'; break;
      case 'date': ok = typeof v === 'string' && DATE_RE.test(v); break;
      case 'time': ok = typeof v === 'string' && TIME_RE.test(v); break;
      case 'datetime': ok = typeof v === 'string' && DT_RE.test(v); break;
      case 'number': ok = typeof v === 'number' && Number.isFinite(v); break;
      case 'int': ok = typeof v === 'number' && Number.isInteger(v); break;
      case 'bool': ok = typeof v === 'boolean'; break;
      case 'enum': ok = typeof v === 'string' && (def.values ?? []).includes(v); break;
      case 'json': ok = true; break;
    }
    if (!ok) issues.push({ table, id, field: name, problem: `invalid ${def.t}${def.t === 'enum' ? ` (${String(v)})` : ''}` });
  }
  for (const k of Object.keys(row)) if (!(k in fields)) issues.push({ table, id, field: k, problem: 'unknown field' });
  return issues;
}

export function validateDataset(ds: Dataset, opts: { referential?: boolean } = {}): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const ids: Partial<Record<TableName, Set<string>>> = {};
  for (const t of TABLE_NAMES) {
    const rows = (ds[t] ?? []) as unknown as Record<string, unknown>[];
    const seen = new Set<string>();
    for (const r of rows) {
      issues.push(...validateRow(t, r));
      const id = String(r.id);
      if (seen.has(id)) issues.push({ table: t, id, field: 'id', problem: 'duplicate id' });
      seen.add(id);
    }
    ids[t] = seen;
  }
  if (opts.referential !== false) {
    for (const t of TABLE_NAMES) {
      const fields = TABLES[t] as Record<string, FieldDef>;
      const refs = Object.entries(fields).filter(([, d]) => d.t === 'ref' && d.ref);
      for (const r of (ds[t] ?? []) as unknown as Record<string, unknown>[]) {
        for (const [name, def] of refs) {
          const v = r[name];
          if (typeof v === 'string' && v && !ids[def.ref as TableName]?.has(v)) issues.push({ table: t, id: String(r.id), field: name, problem: `dangling reference → ${def.ref}` });
        }
      }
    }
  }
  return issues;
}
